/**
 * General Analytics Integration Module
 * 
 * This module provides a safe, reliable way to track events across multiple analytics
 * providers. It pushes events into a universal data layer and forwards selected events
 * directly to external providers such as GTM, GA4, Facebook Pixel, and PostHog.
 * 
 * Key Features:
 * - Fail-safe event tracking with built-in error handling, validation, and debugging.
 * - Automatic validation of event names and parameters.
 * - Detailed logging in development mode.
 * - TypeScript-like event definitions with required/optional parameters.
 * - Automatic addition of common tracking parameters (timestamp, page URL, referrer, etc.).
 * - Multi-provider support: GTM, GA4, Facebook Pixel, and PostHog for feature flags & analytics.
 * 
 * Environment Variables (set these in your production environment or .env file):
 *   REACT_APP_GTM_ID                    // GTM container ID (e.g., "GTM-KGR4NV5Z")
 *   REACT_APP_GA4_ID                    // GA4 Measurement ID (e.g., "G-0G91KXVVQF")
 *   REACT_APP_FACEBOOK_PIXEL_ID         // Facebook Pixel ID (e.g., "118452039498342")
 *   REACT_APP_PUBLIC_POSTHOG_KEY        // PostHog project API key
 *   REACT_APP_PUBLIC_POSTHOG_HOST       // PostHog ingest host (e.g., "https://e.botspot.trade")
 *   REACT_APP_PUBLIC_POSTHOG_UI_HOST    // PostHog UI host (e.g., "https://us.posthog.com")
 * 
 * Usage:
 *   1. Ensure your index.html includes no hard-coded provider scripts.
 *      Instead, these scripts are loaded dynamically (for GA4 and Facebook Pixel) or via initAnalytics.
 *   2. Call initAnalytics() once at application startup.
 *   3. Use pushEvent({ ... }) anywhere in your app to record events.
 * 
 * Example:
 *   import { pushEvent, ANALYTICS_EVENTS } from './utils/analytics';
 *   pushEvent({
 *     event: ANALYTICS_EVENTS.PAYMENT_SUCCESS,
 *     sessionId: 'cs_test_12345',
 *     value: 49.99,
 *     currency: 'USD',
 *     userEmail: 'user@example.com'
 *   });
 * 
 * This call pushes the event to the universal data layer and forwards it directly to
 * external providers as configured.
 */

/* ==================== External Dependencies ==================== */

// PostHog for analytics and feature flags
import posthog from 'posthog-js';
import { isStorageAvailable } from './browserStorage';

/* ==================== Provider Environment Configuration ==================== */

const isTestEnv = process.env.NODE_ENV === 'test';

const getEnvValue = (key, fallback = undefined) => {
  const value = process.env[key];
  if (value) {
    return value;
  }

  return isTestEnv ? fallback : undefined;
};

export const GTM_ID = process.env.REACT_APP_GTM_ID;
export const GA4_ID = getEnvValue('REACT_APP_GA4_ID', 'G-FH2EX9LXR0');
export const FACEBOOK_PIXEL_ID = getEnvValue(
  'REACT_APP_FACEBOOK_PIXEL_ID',
  '118452039498342'
);
export const POSTHOG_KEY = process.env.REACT_APP_PUBLIC_POSTHOG_KEY;
export const POSTHOG_HOST = process.env.REACT_APP_PUBLIC_POSTHOG_HOST || 'https://e.botspot.trade';
export const POSTHOG_UI_HOST = process.env.REACT_APP_PUBLIC_POSTHOG_UI_HOST || 'https://us.posthog.com';

const POSTHOG_CRITICAL_EVENT_NAMES = new Set([
  '$pageview',
  'PageView',
  'Lead',
  '$feature_flag_called',
  'experiment_exposure',
  'upgrade_to_pro_success',
  'begin_checkout',
  'checkout_page_loaded',
  'lead_captured',
  'get_started_click',
  'sales_ai_prompt_submitted',
]);

const isPostHogCriticalEvent = (eventName) => POSTHOG_CRITICAL_EVENT_NAMES.has(eventName);

// Google Ads Conversion Configuration
export const GOOGLE_ADS_CONVERSION_ID = getEnvValue('REACT_APP_GOOGLE_ADS_CONVERSION_ID', 'AW-10985667985');
export const GOOGLE_ADS_PURCHASE_LABEL = getEnvValue(
  'REACT_APP_GOOGLE_ADS_PURCHASE_LABEL',
  '705081-H8eCJnv6jy5Q_9c3QwDtQJgz4'
);
export const GOOGLE_ADS_QA_PURCHASE_LABEL = getEnvValue('REACT_APP_GOOGLE_ADS_QA_PURCHASE_LABEL');
export const GOOGLE_ADS_LEAD_LABEL = getEnvValue('REACT_APP_GOOGLE_ADS_LEAD_LABEL', '7453205467');

const REDACTED_ANALYTICS_VALUE = '[redacted]';
const SENSITIVE_QUERY_PARAM_KEYS = new Set([
  'accesstoken',
  'auth',
  'authtoken',
  'autologintoken',
  'bearer',
  'cookie',
  'idtoken',
  'jwt',
  'logintoken',
  'refreshtoken',
  'token',
]);

const normalizeSensitiveKey = (key) => String(key || '').replace(/[_-]/g, '').toLowerCase();

const isSensitiveAnalyticsKey = (key) => {
  const normalized = normalizeSensitiveKey(key);
  return (
    SENSITIVE_QUERY_PARAM_KEYS.has(normalized) ||
    normalized.endsWith('token') ||
    normalized.endsWith('secret') ||
    normalized.endsWith('jwt')
  );
};

const isUrlLikeAnalyticsKey = (key) => {
  const normalized = normalizeSensitiveKey(key);
  return (
    normalized.includes('url') ||
    normalized.includes('href') ||
    normalized.includes('location') ||
    normalized.includes('referrer') ||
    normalized.includes('referer')
  );
};

export const sanitizeAnalyticsUrl = (value) => {
  if (!value || typeof value !== 'string') {
    return value;
  }

  try {
    const isAbsoluteUrl = /^[a-z][a-z\d+\-.]*:/i.test(value);
    const origin = typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://botspot.invalid';
    const url = new URL(value, origin);
    let changed = false;

    Array.from(url.searchParams.keys()).forEach((key) => {
      if (isSensitiveAnalyticsKey(key)) {
        url.searchParams.set(key, REDACTED_ANALYTICS_VALUE);
        changed = true;
      }
    });

    if (!changed) {
      return value;
    }

    return isAbsoluteUrl ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return value;
  }
};

export const sanitizeAnalyticsProperties = (properties = {}) => {
  if (!properties || typeof properties !== 'object') {
    return properties;
  }

  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) => {
      if (isSensitiveAnalyticsKey(key)) {
        return [key, REDACTED_ANALYTICS_VALUE];
      }

      if (typeof value === 'string' && isUrlLikeAnalyticsKey(key)) {
        return [key, sanitizeAnalyticsUrl(value)];
      }

      return [key, value];
    })
  );
};

export const sanitizePostHogEvent = (event) => {
  if (!event || typeof event !== 'object') {
    return event;
  }

  const sanitizedProperties = sanitizeAnalyticsProperties(event.properties || {});

  const sdkToken = event.properties?.token;
  const isPostHogProjectToken = typeof sdkToken === 'string' && sdkToken.startsWith('phc_');

  if (sdkToken === POSTHOG_KEY || isPostHogProjectToken) {
    sanitizedProperties.token = event.properties.token;
  }

  return {
    ...event,
    properties: sanitizedProperties,
  };
};

const currentPageUrl = () => sanitizeAnalyticsUrl(window.location.href);
const currentReferrer = () => sanitizeAnalyticsUrl(document.referrer);

const LOCAL_ANALYTICS_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);
const BOTSPOT_ANALYTICS_HOSTS = new Set(['botspot.trade', 'www.botspot.trade']);
const BOTSPOT_DEV_ANALYTICS_HOSTS = new Set(['dev.botspot.trade']);
const LUMIBOT_ANALYTICS_HOSTS = new Set(['lumibot.lumiwealth.com', 'www.lumibot.lumiwealth.com']);

const getCurrentHostname = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const directHostname = window.location?.hostname;
  if (directHostname) {
    return directHostname;
  }

  try {
    return new URL(window.location?.href || '').hostname;
  } catch {
    return '';
  }
};

const getCurrentPathname = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const directPathname = window.location?.pathname;
  if (directPathname) {
    return directPathname;
  }

  try {
    return new URL(window.location?.href || '').pathname;
  } catch {
    return '';
  }
};

const getSiteSurface = (hostname) => {
  if (BOTSPOT_ANALYTICS_HOSTS.has(hostname)) {
    return 'botspot_web';
  }

  if (BOTSPOT_DEV_ANALYTICS_HOSTS.has(hostname) || LOCAL_ANALYTICS_HOSTS.has(hostname)) {
    return 'botspot_dev';
  }

  if (LUMIBOT_ANALYTICS_HOSTS.has(hostname) || hostname.endsWith('.lumiwealth.com')) {
    return 'lumibot_docs';
  }

  return hostname ? 'other' : 'unknown';
};

const isAutomationUserAgent = () => {
  const userAgent =
    (typeof navigator !== 'undefined' && navigator.userAgent) ||
    (typeof window !== 'undefined' && window.navigator?.userAgent) ||
    '';

  return /HeadlessChrome|Playwright|Puppeteer|Chrome-Lighthouse|bot|crawler|spider/i.test(userAgent);
};

export const getAnalyticsContextProperties = () => {
  const hostname = getCurrentHostname();
  const pathname = getCurrentPathname();
  const siteSurface = getSiteSurface(hostname);
  const isDevelopmentTraffic = siteSurface === 'botspot_dev';

  return {
    analytics_host: hostname,
    analytics_pathname: pathname,
    site_surface: siteSurface,
    traffic_environment: isDevelopmentTraffic ? 'development' : 'production',
    is_automation_traffic: isAutomationUserAgent(),
  };
};

/* ==================== Analytics Event Definitions ==================== */

/**
 * ANALYTICS_EVENTS:
 * Canonical event definitions used throughout the application.
 *
 * IMPORTANT: Always pass the event object (e.g., ANALYTICS_EVENTS.PAYMENT_SUCCESS) to pushEvent.
 * NEVER use .name (e.g., ANALYTICS_EVENTS.PAYMENT_SUCCESS.name) in your code.
 * This ensures event metadata and documentation are preserved and prevents analytics bugs.
 * 
 * 🚨 CRITICAL: These events are used for Google Ads and Analytics conversion tracking.
 * Changes to these event names will break conversion tracking and ad attribution.
 * Never change these event names without updating all tracking systems first.
 */
export const ANALYTICS_EVENTS = {
  // --- PAGE & NAVIGATION ---
  PAGE_VIEW: {
    name: 'page_view',
    description: `
      Fired automatically on every page load and route change.
      Tracks user navigation patterns and session starts.
      Always includes current URL, referrer, and timestamp.
    `,
    requiredParams: ['page_path', 'page_title'],
    optionalParams: ['referrer', 'userId'],
    example: '{ event: page_view, page_path: "/home", page_title: "Home" }',
    relatedEvents: ['login_clicked', 'user_authenticated'],
    notes: 'Typically fired on route change in a useEffect.'
  },
  HOMEPAGE_AGENTS_CALLOUT_CLICK: {
    name: 'homepage_agents_callout_click',
    description: `
      Fired when a user clicks the "Use BotSpot in your AI assistant"
      callout on the homepage that drives traffic to /agents (where the
      Claude/ChatGPT/Cursor MCP install flow lives).
      Used to attribute MCP directory installs to the homepage funnel
      vs other top-of-funnel sources.
    `,
    requiredParams: ['source'],
    optionalParams: ['destination'],
    example: '{ event: homepage_agents_callout_click, source: "home_lovable", destination: "/agents" }',
    relatedEvents: ['page_view'],
    notes: 'Click handler is fire-and-forget; never blocks navigation if analytics fails.'
  },
  // --- AUTHENTICATION ---
  LOGIN_CLICKED: {
    name: 'login_clicked',
    description: `
      Fired when a user initiates the login process.
      Useful for analyzing login attempts.
    `,
    requiredParams: ['method', 'source'],
    optionalParams: ['referrer'],
    example: '{ event: login_clicked, method: "auth0", source: "/pricing" }',
    relatedEvents: ['user_authenticated'],
    notes: 'Fired before authentication begins.'
  },
  USER_AUTHENTICATED: {
    name: 'user_authenticated',
    description: `
      Fired when a user successfully authenticates.
      Critical for tracking sessions and login success.
    `,
    requiredParams: ['userId', 'userEmail'],
    optionalParams: ['loginMethod', 'source'],
    example: '{ event: user_authenticated, userId: "123", userEmail: "user@example.com" }',
    relatedEvents: ['login_clicked'],
    notes: 'Fired after login completes.'
  },
  // --- SUBSCRIPTION & PAYMENT ---
  SUBSCRIPTION_CTA_CLICKED: {
    name: 'subscription_cta_clicked',
    description: `
      Fired when a user clicks a subscription/upgrade call-to-action.
      Helps track entry points into subscription funnels.
    `,
    requiredParams: ['source', 'feature'],
    optionalParams: ['plan', 'userId'],
    example: '{ event: subscription_cta_clicked, source: "ai_bot_builder" }',
    relatedEvents: ['begin_checkout', 'premium_feature_prompt'],
    notes: 'Fired before redirecting to the sales page.'
  },
  
  // 🚨 CRITICAL: CONSOLIDATED PAYMENT SUCCESS EVENT
  // This is the ONLY payment success event that should be used going forward
  // It replaces all previous payment_success_* events to consolidate tracking
  PAYMENT_SUCCESS: {
    name: 'payment_success',
    description: `
      🚨 CRITICAL: The canonical payment success event for ALL purchases.
      This event is used by Google Ads, GA4, and Facebook Pixel for conversion tracking.
      NEVER change this event name - it will break conversion tracking.
      Use plan_type parameter to differentiate between plan types.
    `,
    requiredParams: ['transaction_id', 'value', 'currency', 'plan_type'],
    optionalParams: ['userId', 'userEmail', 'source', 'customerId', 'paymentMethodId'],
    example: '{ event: payment_success, transaction_id: "pi_123", value: 99.00, currency: "USD", plan_type: "starter" }',
    relatedEvents: ['begin_checkout', 'payment_error'],
    notes: '🚨 CRITICAL: Used for conversion tracking. Never change this event name.',
    tracking: {
      ga4: 'purchase',
      facebook: 'Purchase', 
      googleAds: 'conversion'
    }
  },
  
  // 🚨 NEW: GENERIC PRO UPGRADE SUCCESS EVENT
  // This event captures ALL pro upgrades regardless of method or source
  UPGRADE_TO_PRO_SUCCESS: {
    name: 'upgrade_to_pro_success',
    description: `
      🎯 CRITICAL: Generic event for ALL pro plan upgrades regardless of method.
      This event should be fired for every successful pro upgrade including:
      - One-click upgrades from saved payment methods
      - Credit card form submissions (CheckoutForm modal)
      - AI Bootcamp page upgrades
      - Any future pro upgrade flows
      Used for complete sales funnel analysis and conversion tracking.
    `,
    requiredParams: ['source', 'upgrade_method'],
    optionalParams: ['transaction_id', 'value', 'currency', 'customerId', 'email', 'paymentMethodId', 'utm_source', 'utm_medium', 'utm_campaign'],
    example: '{ event: upgrade_to_pro_success, source: "/upgrade-to-pro", upgrade_method: "one_click", transaction_id: "pi_123", value: 1198, currency: "USD" }',
    relatedEvents: ['one_click_upgrade_pro_success', 'payment_success'],
    notes: '🎯 Fire this for EVERY pro upgrade to track complete funnel regardless of specific method',
    tracking: {
      ga4: 'purchase',
      facebook: 'Purchase',
      googleAds: 'conversion'
    }
  },
  
  PAYMENT_ERROR: {
    name: 'payment_error',
    description: 'Fired when a payment processing error or verification failure occurs.',
    requiredParams: ['errorType'],
    optionalParams: ['sessionId', 'errorMessage'],
    example: '{ event: payment_error, errorType: "verification_failed" }',
    relatedEvents: ['payment_success'],
    notes: 'Fired when a payment fails.'
  },
  CHECKOUT_CATALOG_MISSING_PRODUCT: {
    name: 'checkout_catalog_missing_product',
    description: 'Triggered when checkout references products or plans not present in the catalog response.',
    requiredParams: ['missingSlugs'],
    optionalParams: ['flow_context', 'cart_item_count'],
    example: '{ event: checkout_catalog_missing_product, missingSlugs: "theta-stocks" }',
    relatedEvents: ['begin_checkout', 'payment_error'],
    notes: 'Helps surface misconfigured product slugs or pricing data before customers attempt payment.'
  },
  PASSWORD_RESET_EMAIL_SENT: {
    name: 'password_reset_email_sent',
    description: 'Fired when a password reset email is successfully sent to a user.',
    requiredParams: ['userEmail'],
    optionalParams: ['success'],
    example: '{ event: password_reset_email_sent, userEmail: "user@example.com" }',
    relatedEvents: [],
    notes: 'Fired after the email is dispatched.'
  },
  PASSWORD_RESET_EMAIL_ERROR: {
    name: 'password_reset_email_error',
    description: 'Fired when sending a password reset email fails.',
    requiredParams: ['errorType', 'userEmail'],
    optionalParams: ['errorMessage'],
    example: '{ event: password_reset_email_error, errorType: "api_error" }',
    relatedEvents: ['password_reset_email_sent'],
    notes: 'Fired on email sending error.'
  },
  // --- UPGRADE TO PRO ---
  UPGRADE_TO_PRO_PAGE_VIEW: {
    name: 'upgrade_to_pro_page_view',
    description: 'Fired when the "Upgrade to Pro" upsell page is viewed.',
    requiredParams: ['source'],
    optionalParams: ['customerId', 'email'],
    example: '{ event: "upgrade_to_pro_page_view", source: "/upgrade-to-pro", customerId: "cus_123" }',
    relatedEvents: ['pro_upgrade_cta_clicked', 'skip_pro_upgrade_clicked'],
    notes: 'Fired when the upsell page loads.'
  },
  SALES_PAGE_VIEW: {
    name: 'sales_page_view',
    description: 'Fired when the main sales page is viewed.',
    requiredParams: ['source'],
    optionalParams: ['userId', 'utm_source', 'utm_medium', 'utm_campaign'],
    example: '{ event: "sales_page_view", source: "/sales", utm_source: "google", utm_medium: "cpc" }',
    relatedEvents: ['begin_checkout', 'lead_captured'],
    notes: 'Fired when the sales page loads.'
  },
  PRO_UPGRADE_CTA_CLICKED: {
    name: 'pro_upgrade_cta_clicked',
    description: 'Fired when the user clicks the "Yes, Upgrade to Pro" button.',
    requiredParams: ['customerId', 'source'],
    optionalParams: ['email'],
    example: '{ event: "pro_upgrade_cta_clicked", customerId: "cus_123", source: "/upgrade-to-pro" }',
    relatedEvents: ['pro_upgrade_successful', 'pro_upgrade_failed'],
    notes: 'Fired on upgrade button click.'
  },
  PRO_UPGRADE_SUCCESSFUL: {
    name: 'pro_upgrade_successful',
    description: 'Fired when the backend confirms the Pro upgrade was successful.',
    requiredParams: ['customerId'],
    optionalParams: ['email', 'subscriptionId'],
    example: '{ event: "pro_upgrade_successful", customerId: "cus_123" }',
    relatedEvents: ['pro_upgrade_cta_clicked'],
    notes: 'Fired after successful API response for upgrade.'
  },
  PRO_UPGRADE_FAILED: {
    name: 'pro_upgrade_failed',
    description: 'Fired when the Pro upgrade attempt fails.',
    requiredParams: ['customerId', 'error_message'],
    optionalParams: ['email', 'error_type'],
    example: '{ event: "pro_upgrade_failed", customerId: "cus_123", error_message: "Payment declined" }',
    relatedEvents: ['pro_upgrade_cta_clicked'],
    notes: 'Fired on API error during upgrade.'
  },
  SKIP_PRO_UPGRADE_CLICKED: {
    name: 'skip_pro_upgrade_clicked',
    description: 'Fired when the user clicks the "No thanks" link on the upsell page.',
    requiredParams: ['customerId', 'source'],
    optionalParams: ['email'],
    example: '{ event: "skip_pro_upgrade_clicked", customerId: "cus_123", source: "/upgrade-to-pro" }',
    relatedEvents: ['upgrade_to_pro_page_view'],
    notes: 'Fired when user chooses not to upgrade.'
  },
  UPGRADE_TO_PRO_PAYMENT_FAILED: 'upgrade_to_pro_payment_failed',
  UPGRADE_TO_PRO_FALLBACK_CLICKED: 'upgrade_to_pro_fallback_clicked',
  UPGRADE_TO_PRO_FALLBACK_FAILED: 'upgrade_to_pro_fallback_failed',
  
  // --- PAYMENT REUSE HOOK ---
  PAYMENT_REUSE_TOKEN_ERROR: {
    name: 'payment_reuse_token_error',
    description: 'Error consuming a temporary payment token via usePaymentReuse hook.',
    requiredParams: ['source', 'errorType', 'errorMessage'],
    optionalParams: ['customerId'],
    example: '{ event: "payment_reuse_token_error", source: "/upgrade-to-pro", errorType: "token_consumption_failed", errorMessage: "Token invalid" }',
  },
  PAYMENT_REUSE_TOKEN_SUCCESS: {
    name: 'payment_reuse_token_success',
    description: 'Successfully consumed a temporary payment token via usePaymentReuse hook.',
    requiredParams: ['source', 'customerId', 'paymentMethodId'],
    optionalParams: [],
    example: '{ event: "payment_reuse_token_success", source: "/upgrade-to-pro", customerId: "cus_123", paymentMethodId: "pm_123" }',
  },

  // --- UPGRADE TO PRO PAGE - SECTION VIEWS & INTERACTIONS ---
  UPGRADE_TO_PRO_BENEFITS_VIEWED: {
    name: 'upgrade_to_pro_benefits_viewed',
    description: 'User viewed the "Benefits" section on the Upgrade to Pro page.',
    requiredParams: ['source'],
    optionalParams: [],
  },
  UPGRADE_TO_PRO_VALUE_VIEWED: {
    name: 'upgrade_to_pro_value_viewed',
    description: 'User viewed the "Value Proposition" section on the Upgrade to Pro page.',
    requiredParams: ['source'],
    optionalParams: [],
  },
  UPGRADE_TO_PRO_PROOF_VIEWED: {
    name: 'upgrade_to_pro_proof_viewed',
    description: 'User viewed the "Proof/Testimonials" section on the Upgrade to Pro page.',
    requiredParams: ['source'],
    optionalParams: [],
  },
  UPGRADE_TO_PRO_FAQ_VIEWED: {
    name: 'upgrade_to_pro_faq_viewed',
    description: 'User viewed the "FAQ" section on the Upgrade to Pro page.',
    requiredParams: ['source'],
    optionalParams: [],
  },
  UPGRADE_TO_PRO_COMPARE_VIEWED: {
    name: 'upgrade_to_pro_compare_viewed',
    description: 'User viewed the "Comparison Table" section on the Upgrade to Pro page.',
    requiredParams: ['source'],
    optionalParams: [],
  },
  UPGRADE_TO_PRO_CHECKOUT_FORM_VIEWED: {
    name: 'upgrade_to_pro_checkout_form_viewed',
    description: 'User viewed the full Stripe checkout form on the Upgrade to Pro page.',
    requiredParams: ['page_path'],
    optionalParams: [],
  },
  UPGRADE_TO_PRO_EXIT_INTENT_MODAL_VIEWED: {
    name: 'upgrade_to_pro_exit_intent_modal_viewed',
    description: 'Exit intent modal was shown on the Upgrade to Pro page.',
    requiredParams: ['source'],
    optionalParams: [],
  },
  UPGRADE_TO_PRO_EXIT_INTENT_MODAL_CTA_CLICKED: {
    name: 'upgrade_to_pro_exit_intent_modal_cta_clicked',
    description: 'User clicked the CTA in the exit intent modal on the Upgrade to Pro page.',
    requiredParams: ['source'],
    optionalParams: [],
  },
  UPGRADE_TO_PRO_ONE_CLICK_CONFIRMATION_MODAL_VIEWED: {
    name: 'upgrade_to_pro_one_click_confirmation_modal_viewed',
    description: 'One-click upgrade confirmation modal was shown.',
    requiredParams: ['source', 'customerId'],
    optionalParams: [],
  },
  UPGRADE_TO_PRO_ONE_CLICK_CONFIRMATION_MODAL_CONFIRMED: {
    name: 'upgrade_to_pro_one_click_confirmation_modal_confirmed',
    description: 'User confirmed the one-click upgrade.',
    requiredParams: ['source', 'customerId'],
    optionalParams: [],
  },
  UPGRADE_TO_PRO_ONE_CLICK_CONFIRMATION_MODAL_CANCELLED: {
    name: 'upgrade_to_pro_one_click_confirmation_modal_cancelled',
    description: 'User cancelled the one-click upgrade from the confirmation modal.',
    requiredParams: ['source', 'customerId'],
    optionalParams: [],
  },
  UPGRADE_TO_PRO_CHOOSE_DIFFERENT_PAYMENT_METHOD_CLICKED: {
    name: 'upgrade_to_pro_choose_different_payment_method_clicked',
    description: 'User clicked "Use a different payment method" on the one-click upgrade section.',
    requiredParams: ['source'],
    optionalParams: [],
  },
  UPGRADE_TO_PRO_SKIP_UPSELL_CLICKED: 'upgrade_to_pro_skip_upsell_clicked', // User from starter signup skips Pro upsell
  STARTER_PLAN_SUCCESS_PASSWORD_EMAIL_INFO: {
    name: 'starter_plan_success_password_email_info',
    description: 'Information event fired on Pro upsell page when coming from starter success, confirming email and password setup instructions.',
    requiredParams: ['source', 'userEmail', 'customerId', 'flow'],
    optionalParams: [],
  },
  UPGRADE_TO_PRO_CTA_CLICKED: {
    name: 'upgrade_to_pro_cta_clicked',
    description: 'User clicked the primary CTA on the upgrade to pro page',
    requiredParams: ['source', 'cta_location'],
    optionalParams: ['cta_type', 'button_text', 'experiment_name', 'experiment_variant'],
    example: '{ event: "upgrade_to_pro_cta_clicked", source: "/upgrade-to-pro", cta_location: "hero", button_text: "Upgrade Now" }',
    relatedEvents: ['payment_success', 'begin_checkout']
  },

  // 🆕 UNIFIED A/B TEST EVENT: Primary CTA clicks from both variants
  UPGRADE_PAGE_PRIMARY_CTA_CLICKED: {
    name: 'upgrade_page_primary_cta_clicked',
    description: 'User clicked the main CTA on upgrade page (Upgrade Now OR Book Call depending on variant)',
    requiredParams: ['source', 'cta_location', 'cta_type', 'experiment_variant'],
    optionalParams: ['experiment_name', 'button_text'],
    example: '{ event: "upgrade_page_primary_cta_clicked", source: "/upgrade-to-pro", cta_location: "hero", cta_type: "direct_upgrade", experiment_variant: "control" }',
    relatedEvents: ['book_expert_call_clicked', 'upgrade_to_pro_cta_clicked']
  },

  UPGRADE_TO_PRO_COUNTDOWN_ZERO: {
    name: 'upgrade_to_pro_countdown_zero',
    description: 'The countdown timer on the Upgrade to Pro page reached zero.',
    requiredParams: ['source', 'page_title'],
    optionalParams: [],
  },

  // --- BOT MANAGEMENT ---
  BOT_CREATED: {
    name: 'bot_created',
    description: `
      Fired when a new bot is successfully created.
      Useful for tracking product engagement.
    `,
    requiredParams: ['botMode', 'isPublic'],
    optionalParams: ['source', 'botName'],
    example: '{ event: bot_created, botMode: "paper", isPublic: true }',
    relatedEvents: ['bot_creation_failed', 'bot_list_viewed'],
    notes: 'Fired upon successful API response.'
  },
  // --- CHECKOUT & TRIAL ---
  BEGIN_CHECKOUT: {
    name: 'begin_checkout',
    description: 'Fired when a user initiates the checkout process, marking the start of a payment conversion funnel.',
    requiredParams: ['userId', 'source'],
    optionalParams: ['button_location', 'plan_type'],
    example: '{ event: begin_checkout, userId: "123", source: "/sales", button_location: "sales_page", plan_type: "starter" }',
    relatedEvents: ['payment_success', 'payment_error'],
    notes: 'Should be fired when checkout is initiated. Also triggers URL tracking for funnel analysis.'
  },
  VIEW_PRICING: {
    name: 'view_pricing',
    description: 'Fired when a user interacts with or hovers over the pricing information.',
    requiredParams: [],
    optionalParams: ['plan'],
    example: '{ event: view_pricing, plan: "starter" }',
    relatedEvents: [],
    notes: 'Use this event to monitor pricing information engagement.'
  },
  // --- CHECKOUT TRACKING ---
  CHECKOUT_COMPLETE: {
    name: 'checkout_complete',
    description: 'Fired when a checkout session is successfully completed and verified.',
    requiredParams: ['userId', 'sessionId', 'plan'],
    optionalParams: ['amount', 'currency', 'source'],
    example: '{ event: checkout_complete, userId: "123", sessionId: "cs_test_...", plan: "starter", amount: 49.99 }',
    relatedEvents: ['begin_checkout', 'checkout_processing', 'checkout_error', 'payment_success'],
    notes: 'Fired after successfully verifying a checkout session.'
  },
  CHECKOUT_PROCESSING: {
    name: 'checkout_processing',
    description: 'Fired when a checkout session is still being processed and needs verification retry.',
    requiredParams: ['sessionId'],
    optionalParams: ['source', 'retryCount'],
    example: '{ event: checkout_processing, sessionId: "cs_test_...", retryCount: 2 }',
    relatedEvents: ['checkout_complete', 'checkout_error'],
    notes: 'Generally fired when waiting for payment processing to complete.'
  },
  CHECKOUT_ERROR: {
    name: 'checkout_error',
    description: 'Fired when checkout session verification fails.',
    requiredParams: ['sessionId', 'error'],
    optionalParams: ['source'],
    example: '{ event: checkout_error, sessionId: "cs_test_...", error: "API Error: Session expired" }',
    relatedEvents: ['checkout_complete', 'checkout_processing'],
    notes: 'Used to monitor checkout failure rates and diagnose issues.'
  },
  CHECKOUT_ALREADY_SUBSCRIBED: {
    name: 'checkout_already_subscribed',
    description: 'User attempted checkout for a product they are already subscribed to.',
    requiredParams: ['source', 'plan_type', 'checkout_type'],
    optionalParams: ['userId', 'conflictingProducts'],
  },
  FALLBACK_CHECKOUT: { // Added
    name: 'fallback_checkout_initiated',
    description: 'User initiated a fallback checkout process (e.g., redirect to Stripe hosted page).',
    requiredParams: ['source', 'plan_type'],
    optionalParams: ['userId', 'user_type'],
  },
  DIRECT_FALLBACK_CHECKOUT: { // Added
    name: 'direct_fallback_checkout_initiated',
    description: 'User initiated a direct fallback checkout (e.g. when Stripe Elements fails to load).',
    requiredParams: ['source', 'plan_type'],
    optionalParams: ['userId', 'user_type'],
  },
  // --- LEAD GENERATION ---
  LEAD_CAPTURED: {
    name: 'lead_captured',
    description: 'Fired when a user submits their contact information before proceeding to checkout.',
    requiredParams: ['email'],
    optionalParams: ['name', 'phone', 'source', 'userId'],
    example: '{ event: lead_captured, email: "user@example.com", name: "John Doe", source: "/sales" }',
    relatedEvents: ['begin_checkout'],
    notes: 'Should be fired when lead information is collected, before redirecting to checkout.'
  },
  SALES_AI_LEAD_CAPTURED: {
    name: 'sales_ai_lead_captured',
    description: 'Lead submitted contact info on AI sales variant before entering the paywall prompt stage.',
    requiredParams: ['email'],
    optionalParams: ['name', 'phone', 'lead_transaction_id', 'source'],
    example: '{ event: sales_ai_lead_captured, email: "user@example.com", lead_transaction_id: "lead_123" }',
    relatedEvents: ['sales_ai_prompt_submitted', 'sales_ai_checkout_open'],
    notes: 'Use alongside LEAD_CAPTURED for granular AI variant funnel tracking.'
  },
  SALES_AI_PROMPT_INVALID: {
    name: 'sales_ai_prompt_invalid',
    description: 'User attempted to generate without providing a valid prompt on the AI sales prompt stage.',
    requiredParams: ['reason'],
    optionalParams: ['email', 'source'],
    example: '{ event: sales_ai_prompt_invalid, reason: "empty_prompt" }',
    relatedEvents: ['sales_ai_prompt_submitted'],
    notes: 'Useful for diagnosing friction before checkout.'
  },
  SALES_AI_PROMPT_SUBMITTED: {
    name: 'sales_ai_prompt_submitted',
    description: 'User submitted a valid prompt from the AI sales prompt stage and is about to open checkout.',
    requiredParams: ['prompt_length'],
    optionalParams: ['email', 'lead_transaction_id', 'source'],
    example: '{ event: sales_ai_prompt_submitted, prompt_length: 120, email: "user@example.com" }',
    relatedEvents: ['sales_ai_checkout_open', 'begin_checkout'],
    notes: 'Should fire immediately before opening the checkout modal.'
  },
  SALES_AI_CHECKOUT_OPEN: {
    name: 'sales_ai_checkout_open',
    description: 'Checkout modal opened from the AI sales variant prompt stage.',
    requiredParams: ['source'],
    optionalParams: ['email', 'lead_transaction_id'],
    example: '{ event: sales_ai_checkout_open, source: "sales_ai_variant" }',
    relatedEvents: ['begin_checkout', 'payment_success'],
    notes: 'Use to measure modal open rates versus prompt submissions.'
  },
  // --- HOMEPAGE CTA TRACKING ---
  GET_STARTED_CLICK: {
    name: 'get_started_click',
    description: 'Fired when a user clicks any "Get Started" CTA on the homepage. Primary conversion metric for homepage A/B tests.',
    requiredParams: ['button_text', 'source', 'destination', 'cta_location'],
    optionalParams: ['user_type', 'experiment_variant', 'is_paying_customer'],
    example: '{ event: get_started_click, button_text: "Start Free 7-Day Trial", source: "/", destination: "/sales", cta_location: "hero" }',
    relatedEvents: ['lead_captured', 'begin_checkout'],
    notes: '🚨 CRITICAL: Primary metric for PostHog A/B tests.'
  },
  // --- PRICING PAGE CTA TRACKING ---
  PRICING_CTA_CLICK: {
    name: 'pricing_cta_click',
    description: 'Fired when a user clicks a plan CTA on the pricing page. Primary conversion metric for pricing A/B tests.',
    requiredParams: ['button_text', 'source', 'destination', 'plan'],
    optionalParams: ['price', 'user_type', 'experiment_variant'],
    example: '{ event: pricing_cta_click, button_text: "Get Started", source: "/pricing", destination: "/checkout/starter-plan", plan: "starter" }',
    relatedEvents: ['begin_checkout', 'payment_success'],
    notes: '🚨 CRITICAL: Primary metric for STARTER_PRICING_ABC A/B test.'
  },
  BOOTCAMP_PATH_SELECTED: {
    name: 'bootcamp_path_selected',
    description: 'Fired when a user chooses live Bootcamp, self-paced Bootcamp, or a fit call from a Bootcamp sales surface.',
    requiredParams: ['source', 'path', 'cta_location'],
    optionalParams: ['productSlug', 'planKey', 'destination', 'userId', 'email'],
    example: '{ event: bootcamp_path_selected, source: "/ai-bot-builder-bootcamp", path: "self_paced", cta_location: "hero_path_card" }',
    relatedEvents: ['pricing_cta_click', 'checkout_started', 'begin_checkout'],
    notes: 'Used to compare live cohort vs self-paced product demand before checkout.'
  },
  BOOTCAMP_PLAN_SELECTED: {
    name: 'bootcamp_plan_selected',
    description: 'Fired when a user changes the live Bootcamp payment plan selector.',
    requiredParams: ['source', 'planKey'],
    optionalParams: ['previousPlanKey', 'userId', 'email'],
    example: '{ event: bootcamp_plan_selected, source: "/ai-bot-builder-bootcamp", planKey: "bootcamp-5mo" }',
    relatedEvents: ['bootcamp_path_selected', 'checkout_started'],
    notes: 'Used to compare upfront, 3-payment, and 5-payment demand.'
  },
  BOOTCAMP_CHECKOUT_STARTED: {
    name: 'bootcamp_checkout_started',
    description: 'Fired immediately before opening checkout for any Bootcamp product.',
    requiredParams: ['source', 'productSlug', 'planKey'],
    optionalParams: ['path', 'cta_location', 'userId', 'email'],
    example: '{ event: bootcamp_checkout_started, source: "/ai-bot-builder-bootcamp/self-paced", productSlug: "ai-bootcamp-self-paced", planKey: "self-paced-launch" }',
    relatedEvents: ['bootcamp_path_selected', 'begin_checkout', 'payment_success'],
    notes: 'Bootcamp-specific checkout start signal that avoids overloading generic checkout_started.'
  },
  // --- COURSE/WORKSHOP/BOOTCAMP EVENTS ---
  COURSE_REGISTER: {
    name: 'course_register',
    description: 'User registered for a course, workshop, or bootcamp session group/cohort.',
    requiredParams: ['courseType', 'groupId', 'isAuthenticated', 'email'],
    optionalParams: [],
    example: '{ event: course_register, courseType: "workshop", groupId: "sg1", isAuthenticated: true, email: "user@example.com" }',
    relatedEvents: ['course_unregister', 'course_add_to_calendar'],
    notes: 'Fired on successful registration.'
  },
  COURSE_UNREGISTER: {
    name: 'course_unregister',
    description: 'User unregistered from a course, workshop, or bootcamp session group/cohort.',
    requiredParams: ['courseType', 'groupId', 'isAuthenticated', 'email'],
    optionalParams: [],
    example: '{ event: course_unregister, courseType: "workshop", groupId: "sg1", isAuthenticated: true, email: "user@example.com" }',
    relatedEvents: ['course_register'],
    notes: 'Fired on successful unregister.'
  },
  COURSE_ADD_TO_CALENDAR: {
    name: 'course_add_to_calendar',
    description: 'User clicked to add a course/workshop/bootcamp session or cohort to their calendar.',
    requiredParams: ['courseType', 'groupId', 'calendarType', 'isAuthenticated', 'email'],
    optionalParams: [],
    example: '{ event: course_add_to_calendar, courseType: "workshop", groupId: "sg1", calendarType: "google", isAuthenticated: true, email: "user@example.com" }',
    relatedEvents: ['course_register'],
    notes: 'Fired when user clicks any add-to-calendar button.'
  },
  COURSE_JOIN_SESSION: {
    name: 'course_join_session',
    description: 'User joined a course, workshop, or bootcamp session.',
    requiredParams: ['courseId', 'userId', 'sessionTime'],
    optionalParams: ['feedback'],
    example: '{ event: course_join_session, courseId: "c1", userId: "123", sessionTime: "2023-10-01T10:00:00Z" }',
    relatedEvents: ['course_register'],
    notes: 'Fired when a user successfully joins a session.'
  },
  // --- SIGNUP & LEAD CAPTURE ---
  SIGNUP_SUBMITTED: {
    name: 'signup_submitted',
    description: 'Fired when a user submits the signup form.',
    requiredParams: ['email', 'source'],
    optionalParams: ['name', 'referrer'],
    example: '{ event: signup_submitted, email: "user@example.com", source: "/signup" }',
    relatedEvents: [],
    notes: 'Fired after the signup form is submitted.'
  },
  LEAD_CAPTURE_SUBMITTED: {
    name: 'lead_capture_submitted',
    description: 'Fired when a user submits the lead capture form.',
    requiredParams: ['email', 'source'],
    optionalParams: ['name', 'phone'],
    example: '{ event: lead_capture_submitted, email: "user@example.com", source: "/pricing" }',
    relatedEvents: [],
    notes: 'Fired after the lead capture form is submitted.'
  },
  EXPERIMENT_VIEWED: {
    name: 'experiment_viewed',
    description: 'Fired when a user is exposed to an A/B test experiment.',
    requiredParams: ['experimentId', 'variationId'],
    optionalParams: ['variationName'],
    example: '{ event: experiment_viewed, experimentId: "exp1", variationId: "control" }',
    relatedEvents: [],
    notes: 'Fired when an experiment is viewed.'
  },
  CHECKOUT_STARTED: {
    name: 'checkout_started',
    description: 'Fired when the checkout process is initiated.',
    requiredParams: ['userId', 'source'],
    optionalParams: ['plan_type'],
    example: '{ event: checkout_started, userId: "123", source: "/sales", plan_type: "starter" }',
    relatedEvents: ['payment_success', 'payment_error'],
    notes: 'Fired when the checkout process starts.'
  },
  CHECKOUT_COMPLETED: {
    name: 'checkout_completed',
    description: 'Fired when the checkout process is successfully completed.',
    requiredParams: ['userId', 'sessionId', 'plan'],
    optionalParams: ['amount', 'currency'],
    example: '{ event: checkout_completed, userId: "123", sessionId: "cs_test_...", plan: "starter", amount: 49.99 }',
    relatedEvents: ['begin_checkout', 'payment_success'],
    notes: 'Fired when the checkout process is completed.'
  },
  PAYMENT_METHOD_SUBMITTED: {
    name: 'payment_method_submitted',
    description: 'Fired when a user submits their payment information.',
    requiredParams: ['userId', 'paymentMethod'],
    optionalParams: ['source'],
    example: '{ event: payment_method_submitted, userId: "123", paymentMethod: "credit_card" }',
    relatedEvents: [],
    notes: 'Fired when the payment method is submitted.'
  },
  // --- DEPLOYMENT EVENTS ---
  DEPLOY_BUTTON_CLICKED: {
    name: 'deploy_button_clicked',
    description: 'Fired when the deploy button is clicked.',
    requiredParams: ['userId', 'source'],
    optionalParams: ['deploymentId'],
    example: '{ event: deploy_button_clicked, userId: "123", source: "/deployments" }',
    relatedEvents: [],
    notes: 'Fired when the deploy button is clicked.'
  },
  DEPLOY_REQUEST_INITIATED: {
    name: 'deploy_request_initiated',
    description: 'Fired when a deployment request is initiated.',
    requiredParams: ['userId', 'deploymentId'],
    optionalParams: ['source'],
    example: '{ event: deploy_request_initiated, userId: "123", deploymentId: "deploy_456" }',
    relatedEvents: [],
    notes: 'Fired when a deployment request is sent to the server.'
  },
  DEPLOY_REQUEST_SUCCESS: {
    name: 'deploy_request_success',
    description: 'Fired when a deployment request is successful.',
    requiredParams: ['userId', 'deploymentId'],
    optionalParams: ['source'],
    example: '{ event: deploy_request_success, userId: "123", deploymentId: "deploy_456" }',
    relatedEvents: [],
    notes: 'Fired when a deployment is successfully started.'
  },
  DEPLOY_REQUEST_FAILED: {
    name: 'deploy_request_failed',
    description: 'Fired when a deployment request fails.',
    requiredParams: ['userId', 'deploymentId', 'errorType'],
    optionalParams: ['errorMessage'],
    example: '{ event: deploy_request_failed, userId: "123", deploymentId: "deploy_456", errorType: "api_error" }',
    relatedEvents: [],
    notes: 'Fired when a deployment request fails.'
  },
  BROKER_CONNECTION_SAVED: {
    name: 'broker_connection_saved',
    description: 'Fired when a user saves or reconnects a vault-backed broker connection.',
    requiredParams: ['broker', 'authMethod', 'paperOrLive'],
    optionalParams: ['source'],
    example: '{ event: broker_connection_saved, broker: "alpaca", authMethod: "api_key", paperOrLive: "paper" }',
    relatedEvents: ['deploy_request_initiated'],
    notes: 'Do not include raw credential values.'
  },
  BROKER_CONNECTION_DELETED: {
    name: 'broker_connection_deleted',
    description: 'Fired when a user deletes a vault-backed broker connection.',
    requiredParams: ['broker'],
    optionalParams: ['source'],
    example: '{ event: broker_connection_deleted, broker: "alpaca" }',
    relatedEvents: [],
    notes: 'Do not include raw credential values.'
  },
  BROKER_CONNECTION_SELECTED_FOR_DEPLOY: {
    name: 'broker_connection_selected_for_deploy',
    description: 'Fired when a user selects a saved broker connection on the deploy page.',
    requiredParams: ['brokerCredentialId'],
    optionalParams: ['broker', 'paperOrLive', 'source'],
    example: '{ event: broker_connection_selected_for_deploy, brokerCredentialId: "uuid", broker: "alpaca" }',
    relatedEvents: ['deploy_request_initiated'],
    notes: 'Metadata only.'
  },
  VIEW_MY_DEPLOYMENTS: {
    name: 'view_my_deployments',
    description: 'Fired when a user views the list of their deployments.',
    requiredParams: ['userId'],
    optionalParams: ['source'],
    example: '{ event: view_my_deployments, userId: "123" }',
    relatedEvents: [],
    notes: 'Fired when the My Deployments page is viewed.'
  },
  VIEW_DEPLOYMENT_STATUS: {
    name: 'view_deployment_status',
    description: 'Fired when a user views the status page of a specific deployment.',
    requiredParams: ['userId', 'deploymentId'],
    optionalParams: ['source'],
    example: '{ event: view_deployment_status, userId: "123", deploymentId: "deploy_456" }',
    relatedEvents: [],
    notes: 'Fired when the status page of a deployment is viewed.'
  },
  STOP_DEPLOYMENT_CLICKED: {
    name: 'stop_deployment_clicked',
    description: 'Fired when the stop deployment button is clicked.',
    requiredParams: ['userId', 'deploymentId'],
    optionalParams: ['source'],
    example: '{ event: stop_deployment_clicked, userId: "123", deploymentId: "deploy_456" }',
    relatedEvents: [],
    notes: 'Fired when the stop deployment button is clicked.'
  },
  STOP_DEPLOYMENT_SUCCESS: {
    name: 'stop_deployment_success',
    description: 'Fired when a deployment is successfully stopped.',
    requiredParams: ['userId', 'deploymentId'],
    optionalParams: ['source'],
    example: '{ event: stop_deployment_success, userId: "123", deploymentId: "deploy_456" }',
    relatedEvents: [],
    notes: 'Fired when a deployment is successfully stopped.'
  },
  STOP_DEPLOYMENT_FAILED: {
    name: 'stop_deployment_failed',
    description: 'Fired when stopping a deployment fails.',
    requiredParams: ['userId', 'deploymentId', 'errorType'],
    optionalParams: ['errorMessage'],
    example: '{ event: stop_deployment_failed, userId: "123", deploymentId: "deploy_456", errorType: "api_error" }',
    relatedEvents: [],
    notes: 'Fired when stopping a deployment fails.'
  },
  DEPLOY_LOGS_VIEWED: {
    name: 'deploy_logs_viewed',
    description: 'Fired when the logs section of a deployment is expanded.',
    requiredParams: ['userId', 'deploymentId'],
    optionalParams: ['source'],
    example: '{ event: deploy_logs_viewed, userId: "123", deploymentId: "deploy_456" }',
    relatedEvents: [],
    notes: 'Fired when the logs section is viewed.'
  },
  DEPLOY_LOGS_COPIED: {
    name: 'deploy_logs_copied',
    description: 'Fired when the logs of a deployment are copied.',
    requiredParams: ['userId', 'deploymentId'],
    optionalParams: ['source'],
    example: '{ event: deploy_logs_copied, userId: "123", deploymentId: "deploy_456" }',
    relatedEvents: [],
    notes: 'Fired when the logs are copied to clipboard.'
  },
  DEPLOYMENT_STATUS_VIEW_CLICKED: {
    name: 'deployment_status_view_clicked',
    description: 'Fired when a user clicks to view the status of a deployment from the deployments table.',
    requiredParams: ['deployment_id', 'source_component', 'clicked_row_index'],
    optionalParams: [],
    example: '{ event: deployment_status_view_clicked, deployment_id: "deploy_456", source_component: "DeploymentsTable", clicked_row_index: 2 }',
    relatedEvents: ['view_deployment_status'],
    notes: 'Fired when the user clicks the strategy name or view button in DeploymentsTable.'
  },
  // AI Bot Builder events
  AI_CODE_COPIED: 'ai_code_copied',
  AI_CODE_RATED: 'ai_code_rated',           // New event for initial rating
  AI_CODE_RATING_CHANGED: 'ai_code_rating_changed', // New event for changing rating
  AI_STRATEGY_GENERATED: 'ai_strategy_generated',
  AI_STRATEGY_REFINED: 'ai_strategy_refined',
  AI_STRATEGY_RENAMED: 'ai_strategy_renamed',
  AI_EXAMPLE_CLICKED: 'ai_example_clicked',
  AI_BUILDER_BACKTEST_CLICKED: 'ai_builder_backtest_clicked',
  AI_BUILDER_DEPLOY_CLICKED: 'ai_builder_deploy_clicked',
  AI_BUILDER_USE_EXISTING_CODE_CLICKED: 'ai_builder_use_existing_code_clicked',
  
  // Strategy Sharing and Public/Private events
  STRATEGY_MADE_PUBLIC: 'strategy_made_public',
  STRATEGY_MADE_PRIVATE: 'strategy_made_private',
  SHARE_BUTTON_CLICKED: 'share_button_clicked',
  STRATEGY_CLONED: 'strategy_cloned',
  PUBLIC_STRATEGY_ACCESSED: 'public_strategy_accessed',
  
  // AI Bot Builder strategy events
  GENERATE_STRATEGY_CLICKED: 'generate_strategy_clicked',
  REFINE_STRATEGY_CLICKED: 'refine_strategy_clicked',
  BACKTEST_STRATEGY_CLICKED: 'backtest_strategy_clicked',
  DEPLOY_STRATEGY_CLICKED: 'deploy_strategy_clicked',
  INPUT_EXPANDED_TOGGLED: 'input_expanded_toggled',
  STRATEGY_ACTION_HOVER: 'strategy_action_hover', // New event for tracking hover interactions
  // Strategy selector events
  STRATEGY_SELECTED: 'strategy_selected',
  NEW_STRATEGY_INITIATED: 'new_strategy_initiated',
  INTERFACE_INTERACTION: 'interface_interaction',
  // New diagram events
  AI_DIAGRAM_VIEWED: 'ai_diagram_viewed',
  AI_DIAGRAM_REGENERATED: 'ai_diagram_regenerated',
  AI_DIAGRAM_DOWNLOADED: 'ai_diagram_downloaded',
  AI_DIAGRAM_REGEN_CANCELLED: 'ai_diagram_regen_cancelled',
  INFO_MODAL_VIEWED: 'info_modal_viewed',
  INFO_MODAL_ACTION_CLICKED: 'info_modal_action_clicked',
  ONE_CLICK_UPGRADE_PRO_ATTEMPT: {
    name: 'one_click_upgrade_pro_attempt',
    description: 'User attempted a one-click Pro upgrade using saved payment method.',
    requiredParams: ['source', 'customerId'],
    optionalParams: ['userId', 'email'],
    example: '{ event: "one_click_upgrade_pro_attempt", source: "/upgrade-to-pro", customerId: "cus_123" }',
  },
  ONE_CLICK_UPGRADE_PRO_SUCCESS: {
    name: 'one_click_upgrade_pro_success',
    description: 'One-click Pro upgrade succeeded.',
    requiredParams: ['source', 'customerId'],
    optionalParams: ['transaction_id'],
    example: '{ event: "one_click_upgrade_pro_success", source: "/upgrade-to-pro", customerId: "cus_123" }',
  },
  ONE_CLICK_UPGRADE_PRO_ERROR: {
    name: 'one_click_upgrade_pro_error',
    description: 'One-click Pro upgrade failed.',
    requiredParams: ['source', 'customerId', 'errorMessage'],
    optionalParams: ['errorType'],
    example: '{ event: "one_click_upgrade_pro_error", source: "/upgrade-to-pro", customerId: "cus_123", errorMessage: "Card declined" }',
  },
  TEMP_PAYMENT_TOKEN_CREATED: {
    name: 'temp_payment_token_created',
    description: 'A temp payment token was created for one-click upgrade.',
    requiredParams: ['source', 'customerId'],
    optionalParams: [],
    example: '{ event: "temp_payment_token_created", source: "/stripe/success", customerId: "cus_123" }',
  },
  TEMP_PAYMENT_TOKEN_ERROR: {
    name: 'temp_payment_token_error',
    description: 'Error creating or consuming a temp payment token.',
    requiredParams: ['source', 'errorType', 'errorMessage'],
    optionalParams: ['customerId'],
    example: '{ event: "temp_payment_token_error", source: "/stripe/success", errorType: "creation_failed", errorMessage: "Token expired" }',
  },
  // --- BACKTESTING EVENTS ---
  BACKTEST_PAGE_VIEW: {
    name: 'backtest_page_view',
    description: 'Fired when the backtest page is viewed.',
    requiredParams: ['aiStrategyId', 'revisionId'],
    optionalParams: ['baseStrategyId'],
    example: '{ event: "backtest_page_view", aiStrategyId: "ai_123", revisionId: "rev_abc" }',
  },
  BACKTEST_STARTED: {
    name: 'backtest_started',
    description: 'Fired when a user initiates a new backtest.',
    requiredParams: ['aiStrategyId', 'revisionId', 'startDate', 'endDate'],
    optionalParams: ['baseStrategyId', 'envVarsCount'],
    example: '{ event: "backtest_started", aiStrategyId: "ai_123", revisionId: "rev_abc", startDate: "2023-01-01", endDate: "2023-12-31" }',
  },
  BACKTEST_STOPPED_BY_USER: {
    name: 'backtest_stopped_by_user',
    description: 'Fired when a user manually stops an ongoing backtest.',
    requiredParams: ['aiStrategyId', 'revisionId'],
    optionalParams: ['baseStrategyId', 'backtestId'],
    example: '{ event: "backtest_stopped_by_user", aiStrategyId: "ai_123", revisionId: "rev_abc", backtestId: "bt_xyz" }',
  },
  BACKTEST_COMPLETED_SUCCESS: {
    name: 'backtest_completed_success',
    description: 'Fired when a backtest completes successfully.',
    requiredParams: ['aiStrategyId', 'revisionId', 'backtestId'],
    optionalParams: ['baseStrategyId', 'durationSeconds'],
    example: '{ event: "backtest_completed_success", aiStrategyId: "ai_123", revisionId: "rev_abc", backtestId: "bt_xyz", durationSeconds: 300 }',
  },
  BACKTEST_FAILED: {
    name: 'backtest_failed',
    description: 'Fired when a backtest fails, either during startup or execution.',
    requiredParams: ['aiStrategyId', 'revisionId', 'errorType'],
    optionalParams: ['baseStrategyId', 'backtestId', 'errorMessage', 'errorCode'],
    example: '{ event: "backtest_failed", aiStrategyId: "ai_123", revisionId: "rev_abc", errorType: "execution_error", errorMessage: "Division by zero." }',
  },
  BACKTEST_DATA_ACCESS_REQUIRED: {
    name: 'backtest_data_access_required',
    description: 'Fired when a backtest is blocked because the user is missing required data-plan access.',
    requiredParams: ['aiStrategyId', 'revisionId'],
    optionalParams: ['baseStrategyId', 'providerSlug', 'missingProducts'],
    example: '{ event: "backtest_data_access_required", aiStrategyId: "ai_123", revisionId: "rev_abc", providerSlug: "theta_data", missingProducts: ["theta-stocks"] }',
  },
  BACKTEST_TRIAL_STARTED: {
    name: 'backtest_trial_started',
    description: 'Fired when the user successfully starts a third-party data trial.',
    requiredParams: ['aiStrategyId', 'revisionId', 'providerSlug'],
    optionalParams: ['baseStrategyId', 'productSlugs'],
    example: '{ event: "backtest_trial_started", aiStrategyId: "ai_123", revisionId: "rev_abc", providerSlug: "theta_data", productSlugs: ["theta-stocks"] }',
  },
  BACKTEST_TRIAL_LIMIT_REACHED: {
    name: 'backtest_trial_limit_reached',
    description: 'Fired when the user attempts to run beyond the free-trial lookback limit.',
    requiredParams: ['aiStrategyId', 'revisionId', 'providerSlug', 'requestedDays', 'limitDays'],
    optionalParams: ['baseStrategyId', 'productSlugs'],
    example: '{ event: "backtest_trial_limit_reached", aiStrategyId: "ai_123", revisionId: "rev_abc", providerSlug: "theta_data", requestedDays: 500, limitDays: 365 }',
  },
  BACKTEST_DATA_PLAN_SELECTION_CHANGED: {
    name: 'backtest_data_plan_selection_changed',
    description: 'Fired whenever a user toggles a data-provider plan within the backtest access modal.',
    requiredParams: ['aiStrategyId', 'revisionId', 'providerSlug', 'productSlug', 'action'],
    optionalParams: ['baseStrategyId', 'planKey', 'bundleGroup', 'selectionCount'],
    example: '{ event: "backtest_data_plan_selection_changed", aiStrategyId: "ai_123", revisionId: "rev_abc", providerSlug: "theta_data", productSlug: "theta-stocks", planKey: "monthly", action: "selected", selectionCount: 1 }',
  },
  BACKTEST_DATA_PLAN_CHECKOUT_STARTED: {
    name: 'backtest_data_plan_checkout_started',
    description: 'Fired when the user proceeds from the backtest plan modal into the unified checkout flow.',
    requiredParams: ['aiStrategyId', 'revisionId', 'providerSlug', 'selectionCount'],
    optionalParams: ['baseStrategyId', 'products'],
    example: '{ event: "backtest_data_plan_checkout_started", aiStrategyId: "ai_123", revisionId: "rev_abc", providerSlug: "theta_data", selectionCount: 2, products: ["theta-stocks", "theta-bundle"] }',
  },
  BACKTEST_LOGS_VIEWED: {
    name: 'backtest_logs_viewed',
    description: 'Fired when a user expands to view backtest logs.',
    requiredParams: ['aiStrategyId', 'revisionId', 'logType'], // logType: 'error' or 'live'
    optionalParams: ['baseStrategyId', 'backtestId'],
    example: '{ event: "backtest_logs_viewed", aiStrategyId: "ai_123", revisionId: "rev_abc", logType: "live", backtestId: "bt_xyz" }',
  },
  BACKTEST_DATE_RANGE_CHANGED: {
    name: 'backtest_date_range_changed',
    description: 'Fired when the user modifies the start or end date for the backtest.',
    requiredParams: ['aiStrategyId', 'revisionId', 'newStartDate', 'newEndDate'],
    optionalParams: ['baseStrategyId', 'rangeMode'],
    example: '{ event: "backtest_date_range_changed", aiStrategyId: "ai_123", revisionId: "rev_abc", newStartDate: "2022-01-01", newEndDate: "2022-12-31" }',
  },
  BACKTEST_HISTORY_ITEM_SELECTED: {
    name: 'backtest_history_item_selected',
    description: 'Fired when a user selects a specific backtest from the history table.',
    requiredParams: ['aiStrategyId', 'revisionId', 'selectedBacktestId'],
    optionalParams: ['baseStrategyId'],
    example: '{ event: "backtest_history_item_selected", aiStrategyId: "ai_123", revisionId: "rev_abc", selectedBacktestId: "bt_prev_xyz" }',
  },
  // --- AI AGENT PAYWALL EVENTS ---
  AI_AGENT_MESSAGE_LIMIT_REACHED: {
    name: 'ai_agent_message_limit_reached',
    description: `
      Fired when a free user reaches their message limit in AI Agent.
      Critical for tracking paywall funnel and conversion optimization.
      Philosophy: We're like MidJourney/Runway - tight limits, high-value output.
    `,
    requiredParams: ['used', 'limit'],
    optionalParams: ['remaining', 'resetWindowDays', 'resetsAt', 'conversationId'],
    example: '{ event: "ai_agent_message_limit_reached", used: 5, limit: 5, remaining: 0, resetWindowDays: 7 }',
    relatedEvents: ['ai_agent_paywall_cta_clicked', 'begin_checkout'],
    notes: 'Critical paywall event. Track this to optimize free tier limits.'
  },
  AI_AGENT_PAYWALL_CTA_CLICKED: {
    name: 'ai_agent_paywall_cta_clicked',
    description: `
      Fired when a user clicks the "Start Free Trial" CTA from the paywall modal.
      Tracks conversion from free limit to checkout funnel.
    `,
    requiredParams: ['cta_type', 'checkoutUrl'],
    optionalParams: ['usage', 'conversationId'],
    example: '{ event: "ai_agent_paywall_cta_clicked", cta_type: "start_trial", checkoutUrl: "/checkout/starter-plan" }',
    relatedEvents: ['ai_agent_message_limit_reached', 'begin_checkout'],
    notes: 'Tracks paywall CTA engagement.'
  },
  AI_AGENT_PAYWALL_DISMISSED: {
    name: 'ai_agent_paywall_dismissed',
    description: 'Fired when a user dismisses the paywall modal without clicking CTA.',
    requiredParams: [],
    optionalParams: ['usage', 'conversationId'],
    example: '{ event: "ai_agent_paywall_dismissed" }',
    relatedEvents: ['ai_agent_message_limit_reached'],
    notes: 'Useful for understanding drop-off at paywall.'
  },
  // --- ONBOARDING EVENTS ---
  ONBOARDING_CTA_CLICKED: {
    name: 'onboarding_cta_clicked',
    description: `
      Fired when a user clicks any CTA on the onboarding page.
      Helps track engagement with onboarding guidance.
    `,
    requiredParams: ['cta_name'],
    optionalParams: ['cta_destination'],
    example: '{ event: onboarding_cta_clicked, cta_name: "Create AI Bot" }',
    relatedEvents: ['page_view'],
    notes: 'Fired for all onboarding CTAs.'
  },
  ONBOARDING_TEMPLATE_DEPLOYED: {
    name: 'onboarding_template_deployed',
    description: 'Fired when a user deploys a template strategy from onboarding.',
    requiredParams: ['templateId'],
    optionalParams: ['strategyId'],
    example: '{ event: onboarding_template_deployed, templateId: "momentum-spy" }',
    relatedEvents: ['onboarding_cta_clicked'],
    notes: 'Fired when template deployment is successful.'
  },
  ONBOARDING_DISMISSED: {
    name: 'onboarding_dismissed',
    description: 'Fired when a user dismisses/skips the onboarding process.',
    requiredParams: ['reason', 'source'],
    optionalParams: ['userId'],
    example: '{ event: onboarding_dismissed, reason: "user_choice", source: "onboarding_page" }',
    relatedEvents: ['onboarding_cta_clicked'],
    notes: 'Fired when user chooses to skip onboarding.'
  },
  OAUTH_SCHWAB_SUCCESS: 'oauth_schwab_success',
  OAUTH_SCHWAB_COPY: 'oauth_schwab_copy',
  OAUTH_SCHWAB_PLATFORM_SELECTED: 'oauth_schwab_platform_selected',
  OAUTH_SCHWAB_EXPIRED: 'oauth_schwab_expired',
  OAUTH_SCHWAB_LOADING: 'oauth_schwab_loading',

  // OAuth Tradier Events
  OAUTH_TRADIER_SUCCESS: 'oauth_tradier_success',
  OAUTH_TRADIER_COPY: 'oauth_tradier_copy',
  OAUTH_TRADIER_EXPIRED: 'oauth_tradier_expired',
  OAUTH_TRADIER_LOADING: 'oauth_tradier_loading',
  
  // OAuth Alpaca Events
  OAUTH_ALPACA_SUCCESS: 'oauth_alpaca_success',
  OAUTH_ALPACA_COPY: 'oauth_alpaca_copy',
  OAUTH_ALPACA_PLATFORM_SELECTED: 'oauth_alpaca_platform_selected',
  OAUTH_ALPACA_EXPIRED: 'oauth_alpaca_expired',
  OAUTH_ALPACA_LOADING: 'oauth_alpaca_loading',
  OAUTH_ALPACA_USE_FOR_DEPLOYMENT: 'oauth_alpaca_use_for_deployment',
  OAUTH_ALPACA_TOKENS_LOADED: 'oauth_alpaca_tokens_loaded',
  OAUTH_ALPACA_INITIATED: 'oauth_alpaca_initiated',
  OAUTH_ALPACA_CLEARED: 'oauth_alpaca_cleared',
  
  // --- SET PASSWORD PAGE ---
  SET_PASSWORD_PAGE_VIEW: {
    name: 'set_password_page_view',
    description: 'Fired when the Set Password page is viewed.',
    requiredParams: ['source'],
    optionalParams: ['email', 'customerId', 'redirectTo'],
    example: '{ event: "set_password_page_view", source: "/set-password", email: "user@example.com" }',
    relatedEvents: ['password_reset_email_sent', 'login_clicked'],
    notes: 'Critical for tracking password setup funnel.'
  },
  
  SET_PASSWORD_PAGE_ERROR_NO_EMAIL: {
    name: 'set_password_page_error_no_email',
    description: 'Fired when Set Password page loads without email parameter.',
    requiredParams: ['source'],
    optionalParams: ['customerId'],
    example: '{ event: "set_password_page_error_no_email", source: "/set-password" }',
    relatedEvents: ['set_password_page_view'],
    notes: 'Indicates broken funnel flow.'
  },
  
  SET_PASSWORD_EMAIL_CLIENT_OPENED: {
    name: 'set_password_email_client_opened',
    description: 'Fired when user clicks to open their email client from Set Password page.',
    requiredParams: ['userEmail', 'emailClient'],
    optionalParams: ['customerId'],
    example: '{ event: "set_password_email_client_opened", userEmail: "user@example.com", emailClient: "gmail" }',
    relatedEvents: ['set_password_page_view'],
    notes: 'Tracks user engagement with password setup flow.'
  },
  
  PASSWORD_SET: {
    name: 'password_set',
    description: 'Fired when user successfully sets their password.',
    requiredParams: ['userEmail', 'source'],
    optionalParams: ['customerId'],
    example: '{ event: "password_set", userEmail: "user@example.com", source: "set_password_page" }',
    relatedEvents: ['set_password_page_view', 'user_authenticated'],
    notes: 'Critical success metric for password setup funnel.'
  },

  // --- SUBSCRIPTION MANAGEMENT ---
  SUBSCRIPTION_MANAGEMENT_VIEWED: {
    name: 'subscription_management_viewed',
    description: 'Fired when the subscription management page is viewed.',
    requiredParams: ['subscriptions_count', 'active_subscriptions'],
    optionalParams: ['source'],
    example: '{ event: "subscription_management_viewed", subscriptions_count: 2, active_subscriptions: 1 }',
    relatedEvents: ['subscription_cancellation_started'],
    notes: 'Tracks engagement with subscription management features.'
  },

  SUBSCRIPTION_CANCELLATION_STARTED: {
    name: 'subscription_cancellation_started',
    description: 'Fired when a user initiates the cancellation process.',
    requiredParams: ['subscription_id', 'plan_name', 'subscription_age_days'],
    optionalParams: ['source'],
    example: '{ event: "subscription_cancellation_started", subscription_id: "sub_123", plan_name: "Starter", subscription_age_days: 30 }',
    relatedEvents: ['cancellation_modal_opened', 'cancellation_survey_completed'],
    notes: 'Critical for tracking cancellation funnel entry.'
  },

  CANCELLATION_MODAL_OPENED: {
    name: 'cancellation_modal_opened',
    description: 'Fired when the cancellation modal is opened.',
    requiredParams: ['subscription_id', 'plan_name'],
    optionalParams: ['source'],
    example: '{ event: "cancellation_modal_opened", subscription_id: "sub_123", plan_name: "Starter" }',
    relatedEvents: ['subscription_cancellation_started', 'cancellation_survey_completed'],
    notes: 'Tracks modal engagement in cancellation flow.'
  },

  CANCELLATION_SURVEY_COMPLETED: {
    name: 'cancellation_survey_completed',
    description: 'Fired when a user completes the cancellation survey.',
    requiredParams: ['subscription_id', 'cancellation_reason', 'pause_preference', 'consultation_interest'],
    optionalParams: ['source'],
    example: '{ event: "cancellation_survey_completed", subscription_id: "sub_123", cancellation_reason: "Too expensive", pause_preference: "30_days", consultation_interest: true }',
    relatedEvents: ['cancellation_modal_opened', 'retention_decision_made'],
    notes: 'Critical for analyzing cancellation reasons and retention opportunities.'
  },

  RETENTION_DECISION_MADE: {
    name: 'retention_decision_made',
    description: 'Fired when a user makes a decision on a retention offer.',
    requiredParams: ['subscription_id', 'decision', 'offer_type', 'outcome'],
    optionalParams: ['source'],
    example: '{ event: "retention_decision_made", subscription_id: "sub_123", decision: "accept", offer_type: "free_months", outcome: "retained" }',
    relatedEvents: ['cancellation_survey_completed', 'subscription_cancellation_completed'],
    notes: 'Critical for measuring retention strategy effectiveness.'
  },

  SUBSCRIPTION_CANCELLATION_COMPLETED: {
    name: 'subscription_cancellation_completed',
    description: 'Fired when the cancellation process is completed.',
    requiredParams: ['subscription_id', 'outcome', 'retention_offer_accepted'],
    optionalParams: ['source'],
    example: '{ event: "subscription_cancellation_completed", subscription_id: "sub_123", outcome: "cancelled", retention_offer_accepted: false }',
    relatedEvents: ['retention_decision_made'],
    notes: 'Final event in cancellation funnel for conversion tracking.'
  },

  SUBSCRIPTION_RESUMED: {
    name: 'subscription_resumed',
    description: 'Fired when a paused subscription is resumed by the user.',
    requiredParams: ['subscription_id', 'plan_name'],
    optionalParams: ['was_paused_until', 'source'],
    example: '{ event: "subscription_resumed", subscription_id: "sub_123", plan_name: "Pro Plan", was_paused_until: "2025-08-06T00:00:00Z" }',
    relatedEvents: ['subscription_management_viewed'],
    notes: 'Tracks when users actively resume their paused subscriptions early.'
  },

  CANCELLATION_SURVEY_PARTIAL_SAVE: {
    name: 'cancellation_survey_partial_save',
    description: 'Fired when partial cancellation survey responses are saved.',
    requiredParams: ['subscription_id', 'step'],
    optionalParams: ['survey_data', 'demo_mode'],
    example: '{ event: "cancellation_survey_partial_save", subscription_id: "sub_123", step: "reason", survey_data: { cancellationReason: "Too expensive" } }',
    relatedEvents: ['cancellation_modal_opened', 'cancellation_survey_abandoned'],
    notes: 'Critical for capturing partial feedback even from incomplete surveys.'
  },

  CANCELLATION_SURVEY_ABANDONED: {
    name: 'cancellation_survey_abandoned',
    description: 'Fired when a user abandons the cancellation survey partway through.',
    requiredParams: ['subscription_id', 'step_abandoned'],
    optionalParams: ['survey_data'],
    example: '{ event: "cancellation_survey_abandoned", subscription_id: "sub_123", step_abandoned: "intent_details", survey_data: { cancellationReason: "Too expensive" } }',
    relatedEvents: ['cancellation_survey_partial_save', 'cancellation_modal_opened'],
    notes: 'Important for understanding where users drop off in the cancellation flow.'
  },

  CANCELLATION_SURVEY_STEP_COMPLETED: {
    name: 'cancellation_survey_step_completed',
    description: 'Fired when a user completes a step in the cancellation survey.',
    requiredParams: ['subscription_id', 'step'],
    optionalParams: ['step_data'],
    example: '{ event: "cancellation_survey_step_completed", subscription_id: "sub_123", step: "reason_details" }',
    relatedEvents: ['cancellation_survey_partial_save'],
    notes: 'Tracks progression through the multi-step cancellation survey.'
  },

  BUYER_FEEDBACK_SURVEY_VIEWED: {
    name: 'buyer_feedback_survey_viewed',
    description: 'Fired when a checkout abandoner opens the invite-only buyer feedback survey.',
    requiredParams: ['source'],
    optionalParams: ['token_id', 'country_code', 'already_submitted'],
    example: '{ event: "buyer_feedback_survey_viewed", source: "cart_abandon_feedback" }',
    relatedEvents: ['buyer_feedback_question_answered', 'buyer_feedback_survey_submitted'],
    notes: 'Used to measure invite click-through and token validity.'
  },

  BUYER_FEEDBACK_QUESTION_ANSWERED: {
    name: 'buyer_feedback_question_answered',
    description: 'Fired when a buyer feedback survey answer changes.',
    requiredParams: ['question_key'],
    optionalParams: ['token_id', 'answer_length', 'selected_count'],
    example: '{ event: "buyer_feedback_question_answered", question_key: "stopped", answer_length: 84 }',
    relatedEvents: ['buyer_feedback_partial_saved'],
    notes: 'Do not send raw survey answer text to analytics.'
  },

  BUYER_FEEDBACK_AI_FOLLOWUP_SHOWN: {
    name: 'buyer_feedback_ai_followup_shown',
    description: 'Fired when the backend returns an AI follow-up question for a short or broad survey answer.',
    requiredParams: ['question_key'],
    optionalParams: ['token_id'],
    example: '{ event: "buyer_feedback_ai_followup_shown", question_key: "stopped" }',
    relatedEvents: ['buyer_feedback_ai_followup_answered'],
    notes: 'Measures whether AI probing improves answer depth.'
  },

  BUYER_FEEDBACK_AI_FOLLOWUP_ANSWERED: {
    name: 'buyer_feedback_ai_followup_answered',
    description: 'Fired when a recipient answers an AI follow-up question.',
    requiredParams: ['question_key'],
    optionalParams: ['token_id', 'answer_length'],
    example: '{ event: "buyer_feedback_ai_followup_answered", question_key: "checkout", answer_length: 71 }',
    relatedEvents: ['buyer_feedback_ai_followup_shown', 'buyer_feedback_partial_saved'],
    notes: 'Do not send raw survey answer text to analytics.'
  },

  BUYER_FEEDBACK_PARTIAL_SAVED: {
    name: 'buyer_feedback_survey_partial_saved',
    description: 'Fired after a partial buyer feedback survey save succeeds.',
    requiredParams: ['question_key'],
    optionalParams: ['token_id', 'answer_count'],
    example: '{ event: "buyer_feedback_partial_saved", question_key: "stopped", answer_count: 2 }',
    relatedEvents: ['buyer_feedback_survey_abandoned', 'buyer_feedback_survey_submitted'],
    notes: 'Backend separately notifies Rob after five minutes of inactivity.'
  },

  BUYER_FEEDBACK_SURVEY_SUBMITTED: {
    name: 'buyer_feedback_survey_submitted',
    description: 'Fired when a checkout abandoner completes the buyer feedback survey.',
    requiredParams: ['source'],
    optionalParams: ['token_id', 'answer_count', 'call_interest'],
    example: '{ event: "buyer_feedback_survey_submitted", source: "cart_abandon_feedback", call_interest: true }',
    relatedEvents: ['buyer_feedback_calendly_clicked'],
    notes: 'The gift card remains manual review, not automated payout.'
  },

  BUYER_FEEDBACK_CALENDLY_CLICKED: {
    name: 'buyer_feedback_calendly_clicked',
    description: 'Fired when a completed survey recipient clicks the feedback call booking link.',
    requiredParams: ['source'],
    optionalParams: ['token_id'],
    example: '{ event: "buyer_feedback_calendly_clicked", source: "cart_abandon_feedback" }',
    relatedEvents: ['buyer_feedback_survey_submitted'],
    notes: 'Used to measure call booking intent.'
  },

  BUYER_FEEDBACK_TOKEN_INVALID: {
    name: 'buyer_feedback_token_invalid',
    description: 'Fired when the feedback page opens without a usable invite token.',
    requiredParams: ['reason'],
    optionalParams: ['source'],
    example: '{ event: "buyer_feedback_token_invalid", reason: "missing_token" }',
    relatedEvents: [],
    notes: 'Useful for catching broken links in email or MMS.'
  },

  BUYER_FEEDBACK_TOKEN_EXPIRED: {
    name: 'buyer_feedback_token_expired',
    description: 'Fired when a feedback invite token exists but is expired.',
    requiredParams: ['reason'],
    optionalParams: ['source'],
    example: '{ event: "buyer_feedback_token_expired", reason: "token_expired" }',
    relatedEvents: [],
    notes: 'Invite links currently expire after seven days.'
  },

  // 🚨 NEW: STARTER PLAN SELECTION TRACKING
  SKIP_TO_STARTER_PLAN: {
    name: 'skip_to_starter_plan',
    description: `
      🎯 CRITICAL: Track when users choose to skip Pro upgrade and stay on Starter plan.
      This helps measure Pro upgrade conversion rates and upsell effectiveness.
    `,
    requiredParams: ['source'],
    optionalParams: ['customerId', 'email', 'upgrade_context', 'utm_source', 'utm_medium', 'utm_campaign'],
    example: '{ event: skip_to_starter_plan, source: "/upgrade-to-pro", upgrade_context: "pro_upsell_page" }',
    relatedEvents: ['upgrade_to_pro_success', 'payment_success'],
    notes: '🎯 Fire when user explicitly chooses to stay on starter plan instead of upgrading'
  },

  STARTER_PLAN_SELECTED: {
    name: 'starter_plan_selected',
    description: `
      🎯 CRITICAL: Track when users actively select the Starter plan.
      This helps measure conversion rates and plan preference analytics.
    `,
    requiredParams: ['source'],
    optionalParams: ['selection_context', 'userId'],
    example: '{ event: starter_plan_selected, source: "/sales", selection_context: "pricing_page" }',
    relatedEvents: ['payment_success'],
    notes: 'Fired when Starter plan is explicitly chosen.'
  },

  // 🚨 NEW: SIGNUP SURVEY COMPLETION TRACKING
  SIGNUP_SURVEY_COMPLETED: {
    name: 'signup_survey_completed',
    description: `
      🎯 CRITICAL: Track completion of the post-signup survey.
      This helps measure onboarding completion and user engagement.
    `,
    requiredParams: ['source', 'email'],
    optionalParams: ['survey_responses', 'completion_time_seconds', 'customerId', 'utm_source', 'utm_medium', 'utm_campaign'],
    example: '{ event: signup_survey_completed, source: "/signup-survey", email: "user@example.com", completion_time_seconds: 45 }',
    relatedEvents: ['payment_success', 'user_authenticated'],
    notes: '🎯 Fire when user completes the signup survey form',
    tracking: {
      ga4: 'survey_complete',
      facebook: 'CompleteRegistration',
      googleAds: 'none'
    }
  },

  SIGNUP_SURVEY_STARTED: {
    name: 'signup_survey_started',
    description: `
      🎯 Track when users start the signup survey (for conversion rate analysis).
    `,
    requiredParams: ['source'],
    optionalParams: ['email', 'customerId', 'utm_source', 'utm_medium', 'utm_campaign'],
    example: '{ event: signup_survey_started, source: "/signup-survey", email: "user@example.com" }',
    relatedEvents: ['signup_survey_completed'],
    notes: '🎯 Fire when user lands on signup survey page',
    tracking: {
      ga4: 'survey_start',
      facebook: 'ViewContent',
      googleAds: 'none'
    }
  },

  SIGNUP_SURVEY_ABANDONED: {
    name: 'signup_survey_abandoned',
    description: `
      🎯 Track when users start but don't complete the signup survey.
    `,
    requiredParams: ['source'],
    optionalParams: ['email', 'customerId', 'time_spent_seconds', 'utm_source', 'utm_medium', 'utm_campaign'],
    example: '{ event: signup_survey_abandoned, source: "/signup-survey", time_spent_seconds: 20 }',
    relatedEvents: ['signup_survey_started'],
    notes: '🎯 Fire when user leaves survey page without completing',
    tracking: {
      ga4: 'survey_abandon',
      facebook: 'Custom',
      googleAds: 'none'
    }
  },

  // 🚨 NEW: EXPERT CALL BOOKING EVENTS FOR A/B TEST
  BOOK_EXPERT_CALL_CLICKED: {
    name: 'book_expert_call_clicked',
    description: `
      🎯 CRITICAL A/B TEST EVENT: Track when users click "Book Expert Call" CTA on upgrade page.
      This is the PRIMARY conversion metric for the upgrade page book call A/B test.
    `,
    requiredParams: ['source', 'cta_location', 'button_text'],
    optionalParams: ['user_type', 'experiment_variant', 'experiment_name', 'is_paying_customer'],
    example: '{ event: book_expert_call_clicked, source: "/upgrade-to-pro", cta_location: "hero", button_text: "Book Expert Call" }',
    relatedEvents: ['expert_call_booked', 'upgrade_to_pro_cta_clicked'],
    notes: '🚨 CRITICAL: Primary metric for PostHog A/B test. Maps to BookCallClick in PostHog.',
    tracking: {
      ga4: 'click',
      facebook: 'Lead',
      googleAds: 'conversion'
    }
  },

  EXPERT_CALL_BOOKED: {
    name: 'expert_call_booked',
    description: `
      🎯 CRITICAL A/B TEST EVENT: Track when users successfully complete booking an expert call.
      This measures actual completion of the book call conversion funnel.
    `,
    requiredParams: ['source', 'calendly_event_type'],
    optionalParams: ['user_email', 'call_date', 'call_time', 'experiment_variant', 'experiment_name'],
    example: '{ event: expert_call_booked, source: "/upgrade-to-pro", calendly_event_type: "botspot-call", call_date: "2025-07-01" }',
    relatedEvents: ['book_expert_call_clicked'],
    notes: '🚨 CRITICAL: Success metric for book call A/B test. Maps to ExpertCallBooked in PostHog.',
    tracking: {
      ga4: 'generate_lead',
      facebook: 'Schedule',
      googleAds: 'conversion'
    }
  },

  CALENDLY_WIDGET_LOADED: {
    name: 'calendly_widget_loaded',
    description: 'Track when Calendly inline widget successfully loads on the page.',
    requiredParams: ['source', 'widget_url'],
    optionalParams: ['load_time_ms'],
    example: '{ event: calendly_widget_loaded, source: "/upgrade-to-pro", widget_url: "https://calendly.com/lumiwealth/botspot-call" }',
    relatedEvents: ['book_expert_call_clicked'],
    notes: 'Technical tracking for Calendly integration health.'
  },

  CALENDLY_WIDGET_ERROR: {
    name: 'calendly_widget_error', 
    description: 'Track when Calendly inline widget fails to load.',
    requiredParams: ['source', 'error_type', 'error_message'],
    optionalParams: ['widget_url'],
    example: '{ event: calendly_widget_error, source: "/upgrade-to-pro", error_type: "network_error", error_message: "Failed to load Calendly" }',
    relatedEvents: ['calendly_widget_loaded'],
    notes: 'Critical for monitoring Calendly integration health.'
  },

  EXPERT_CALL_BOOKING_SUCCESS_PAGE_VIEWED: {
    name: 'expert_call_booking_success_page_viewed',
    description: 'Track when users reach the call booking success page after completing Calendly booking.',
    requiredParams: ['source', 'referrer'],
    optionalParams: ['booking_confirmed', 'user_email'],
    example: '{ event: expert_call_booking_success_page_viewed, source: "/call-booking-success", referrer: "calendly.com" }',
    relatedEvents: ['expert_call_booked'],
    notes: 'Confirms successful completion of call booking flow.'
  },

  // 🚨 CRITICAL A/B TEST MAPPING - UPGRADE PAGE BOOK CALL TEST 🚨
  upgrade_page_primary_cta_clicked: 'UpgradePagePrimaryCTA', // 🚨 CRITICAL: Unified A/B test primary metric
  book_expert_call_clicked: 'UpgradePageBookCallClicked', // 🚨 CRITICAL: Primary metric for upgrade page A/B test
  expert_call_booked: 'UpgradePageCallBooked', // 🚨 CRITICAL: Success metric for call booking funnel
  calendly_widget_loaded: 'CalendlyLoaded',
  calendly_widget_error: 'CalendlyError',
  expert_call_booking_success_page_viewed: 'CallBookingSuccess',
  
};

/* ==================== Validation & Normalization Functions ==================== */

/**
 * Build a set of valid event names for fast lookup.
 * @private
 */
/**
 * Resolves an event name from ANALYTICS_EVENTS.
 * Supports both string keys and event objects.
 * 
 * Example:
 *   getEventName('PAGE_VIEW') => 'page_view'
 *   getEventName(ANALYTICS_EVENTS.PAGE_VIEW) => 'page_view'
 * 
 * @private
 * @param {string|Object} eventKey - Event key or event object.
 * @returns {string} Resolved event name or 'unknown_event'.
 */
const getEventName = (eventKey) => {
  try {
    if (typeof eventKey === 'object' && eventKey !== null && eventKey.name) {
      return eventKey.name;
    }
    if (typeof eventKey === 'string' && ANALYTICS_EVENTS[eventKey]) {
      return ANALYTICS_EVENTS[eventKey].name;
    }
    if (typeof eventKey === 'string') {
      return eventKey;
    }
    return 'unknown_event';
  } catch {
    return 'unknown_event';
  }
};

/* ==================== PostHog Integration ==================== */

// PostHog instance will be initialized in initAnalytics
let posthogInitialized = false;
let posthogInitStarted = false;
let posthogMonitorStarted = false;
let pendingPostHogAttributes = {};

const replacePendingPostHogAttributes = (attributes = {}) => {
  if (attributes && Object.keys(attributes).length > 0) {
    const removedAttributeKeys = Object.keys(pendingPostHogAttributes)
      .filter((key) => !Object.prototype.hasOwnProperty.call(attributes, key));
    pendingPostHogAttributes = { ...attributes };
    return { attributes: pendingPostHogAttributes, removedAttributeKeys };
  }

  return { attributes: pendingPostHogAttributes, removedAttributeKeys: [] };
};

const getPostHogPersistence = () => (
  isStorageAvailable('localStorage') ? 'localStorage+cookie' : 'cookie'
);

const getPostHogClient = ({ initialize = false, attachToWindow = true } = {}) => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (initialize && POSTHOG_KEY && !posthogInitialized && !posthogInitStarted) {
    initPostHog();
  }

  const importedClient = posthog && typeof posthog.capture === 'function' ? posthog : null;
  const client = window.posthog || importedClient;

  // posthog-js does not always expose itself on window before the loaded callback.
  // Use the imported singleton so early custom metrics are queued instead of dropped.
  if (client && attachToWindow && !window.posthog) {
    window.posthog = client;
  }

  return client;
};

const resetPersistedPostHogIdentityForAnonymous = (posthogClient, attributes) => {
  if (
    attributes?.is_authenticated !== false ||
    attributes?.user_id ||
    attributes?.email ||
    !posthogClient?.reset
  ) {
    return;
  }

  const persistedProperties = posthogClient.persistence?.props || {};
  const persistedUserId = posthogClient.get_property?.('user_id') ?? persistedProperties.user_id;
  const persistedEmail = posthogClient.get_property?.('email') ?? persistedProperties.email;
  const wasAuthenticated = posthogClient.get_property?.('is_authenticated')
    ?? persistedProperties.is_authenticated;

  if (wasAuthenticated === true || persistedUserId || persistedEmail) {
    posthogClient.reset();
  }
};

/**
 * initPostHog:
 * Initializes the PostHog client for analytics and feature flags.
 * 
 * @public
 * @param {Object} attributes - User attributes for targeting
 */
export const initPostHog = (attributes = {}) => {
  if (!POSTHOG_KEY) {
    console.warn('POSTHOG_KEY not set – PostHog provider disabled');
    return;
  }

  try {
    const {
      attributes: nextAttributes,
      removedAttributeKeys,
    } = replacePendingPostHogAttributes(attributes);

    if (posthogInitialized || posthogInitStarted) {
      const posthogClient = getPostHogClient({ attachToWindow: true });
      resetPersistedPostHogIdentityForAnonymous(posthogClient, nextAttributes);
      removedAttributeKeys.forEach((key) => posthogClient?.unregister?.(key));
      if (Object.keys(nextAttributes).length > 0 && posthogClient?.register) {
        posthogClient.register(nextAttributes);
      }
      return;
    }

    posthogInitStarted = true;

    // Initialize PostHog
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: POSTHOG_UI_HOST,
      // Enable feature flags
      enable_feature_flags: true,
      // PostHog Cloud currently returns empty /flags payloads for this app's
      // base64-compressed SDK requests; JSON transport preserves flag variants.
      disable_compression: true,
      advanced_feature_flags_dedup_per_session: true,
      // Enable session recording (optional - you can disable this)
      enable_recording: true,
      // Let PostHog handle SPA pageviews and pageleaves automatically
      capture_pageview: 'history_change',
      capture_pageleave: true,
      // Debug mode - only in development and only when specifically needed
      debug: false, // Disabled to reduce log noise
      // Privacy settings
      respect_dnt: true,
      before_send: sanitizePostHogEvent,
      // Advanced settings
      persistence: getPostHogPersistence(),
      cross_subdomain_cookie: false,
      // Disable autocapture to reduce noise
      autocapture: false,
      loaded: function(posthog_instance) {
        resetPersistedPostHogIdentityForAnonymous(posthog_instance, pendingPostHogAttributes);
        // Set initial attributes if provided
        if (Object.keys(pendingPostHogAttributes).length > 0) {
          posthog_instance.register(pendingPostHogAttributes);
        }
        posthogInitialized = true;
        console.debug('PostHog provider initialized');

        // 🚨 IMMEDIATE VERIFICATION: Check if PostHog is properly attached to window
        console.debug('🔍 [PostHog] Immediate verification after init:', {
          hasWindowPosthog: !!window.posthog,
          hasImportedPosthog: !!posthog,
          windowPosthogType: typeof window.posthog,
          importedPosthogType: typeof posthog,
          windowHasCapture: !!(window.posthog && typeof window.posthog.capture === 'function'),
          importedHasCapture: !!(posthog && typeof posthog.capture === 'function'),
          areTheSame: window.posthog === posthog
        });
        
        // If window.posthog is missing but imported posthog exists, manually set it
        if (!window.posthog && posthog && typeof posthog.capture === 'function') {
          console.warn('🔧 [PostHog] window.posthog missing, manually setting from imported posthog');
          window.posthog = posthog;
        }
        
        if (!posthogMonitorStarted) {
          posthogMonitorStarted = true;
          // 🚨 DEBUG: Set up PostHog monitoring
          console.debug('🔍 [PostHog] Setting up monitoring for window.posthog...');

          // Monitor PostHog every 2 seconds to see if it gets wiped out
          const postHogMonitor = setInterval(() => {
            if (!window.posthog) {
              console.error('🚨 [PostHog] CRITICAL: window.posthog was WIPED OUT! Something deleted it.');
              console.debug('🔍 [PostHog] Window keys containing "posthog":', Object.keys(window).filter(k => k.toLowerCase().includes('posthog')));

              // Try to restore it
              if (posthog && typeof posthog.capture === 'function') {
                console.debug('🔄 [PostHog] Attempting to restore window.posthog from imported posthog...');
                window.posthog = posthog;
              }
            } else if (typeof window.posthog.capture !== 'function') {
              console.error('🚨 [PostHog] CRITICAL: window.posthog exists but is corrupted (no capture function)');
              console.debug('🔍 [PostHog] window.posthog type:', typeof window.posthog);
              console.debug('🔍 [PostHog] window.posthog keys:', Object.keys(window.posthog || {}));
            }
          }, 2000);

          // Stop monitoring after 1 minute
          setTimeout(() => {
            clearInterval(postHogMonitor);
            console.debug('🔍 [PostHog] Monitoring stopped');
          }, 60000);
        }
      }
    });

  } catch (initErr) {
    posthogInitStarted = false;
    console.error('PostHog provider initialization failed:', initErr);
  }
};

export const resetAnalyticsIdentity = () => {
  pendingPostHogAttributes = {};
  const posthogClient = (typeof window !== 'undefined' && window.posthog) || posthog;
  posthogClient?.reset?.();
};

/**
 * getFeatureFlag:
 * Gets a feature flag value from PostHog
 * 
 * @public
 * @param {string} flagKey - The feature flag key
 * @param {any} defaultValue - Default value if flag is not found
 * @returns {any} - The feature flag value
 */
export const getFeatureFlag = (flagKey, defaultValue = false) => {
  const posthogClient = getPostHogClient({ initialize: true });

  if (!posthogClient) {
    console.warn(`PostHog not ready when accessing feature flag ${flagKey}`);
    return defaultValue;
  }
  
  try {
    if (typeof posthogClient.getFeatureFlag === 'function') {
      return posthogClient.getFeatureFlag(flagKey) ?? defaultValue;
    } else {
      console.warn(`PostHog getFeatureFlag not available for ${flagKey}`);
      return defaultValue;
    }
  } catch (error) {
    console.error(`Error getting feature flag ${flagKey}:`, error);
    return defaultValue;
  }
};

/**
 * Captures a PostHog-only event without routing it through GTM/GA/Facebook.
 * This is used for feature flag exposure events where PostHog expects exact
 * event/property names such as $feature_flag_called and $feature/<flag>.
 *
 * @public
 * @param {string|Object} eventName - Event name or ANALYTICS_EVENTS object.
 * @param {Object} properties - PostHog event properties.
 * @returns {boolean} Whether the event was handed to PostHog synchronously.
 */
export const capturePostHogEvent = (eventName, properties = {}) => {
  const resolvedEventName = getEventName(eventName);

  try {
    if (typeof window === 'undefined') {
      return false;
    }

    const timestamp = new Date().toISOString();
    const insertId = properties.$insert_id || [
      resolvedEventName,
      properties.$feature_flag || properties.feature_flag || 'event',
      properties.$feature_flag_response || properties.feature_flag_value || properties.variant || 'value',
      timestamp,
      Math.random().toString(36).slice(2),
    ].join(':');
    const eventProperties = sanitizeAnalyticsProperties({
      timestamp,
      page_location: currentPageUrl(),
      page_referrer: currentReferrer(),
      page_title: document.title,
      ...getAnalyticsContextProperties(),
      ...properties,
      $insert_id: insertId,
      $source: properties.$source || 'posthog_sdk_exposure',
      $timestamp: properties.$timestamp || timestamp,
    });

    const posthogClient = getPostHogClient({ initialize: true });

    if (posthogClient && typeof posthogClient.capture === 'function') {
      posthogClient.capture(resolvedEventName, eventProperties, { send_instantly: true });
      return true;
    }

    if (POSTHOG_KEY) {
      initPostHog();
      setTimeout(() => {
        if (window.posthog && typeof window.posthog.capture === 'function') {
          window.posthog.capture(resolvedEventName, eventProperties, { send_instantly: true });
        }
      }, 100);
    }
  } catch (error) {
    console.error(`❌ [PostHog] SDK capture failed for ${resolvedEventName}:`, error);
  }

  return false;
};

/**
 * isFeatureEnabled:
 * Checks if a feature flag is enabled
 * 
 * @public
 * @param {string} flagKey - The feature flag key
 * @returns {boolean} - Whether the feature is enabled
 */
export const isFeatureEnabled = (flagKey) => {
  return getFeatureFlag(flagKey, false) === true;
};

/* ==================== User Identification Across Platforms ==================== */

/**
 * setUserIdentity:
 * Sets user identity consistently across all analytics platforms including PostHog
 * 
 * @public
 * @param {Object} userData - User data for identification
 * @param {string} userData.userId - User's ID
 * @param {string} userData.userEmail - User's email
 * @param {boolean} userData.isAuthenticated - Whether user is authenticated
 * @param {boolean} userData.isPaying - Whether user is a paying customer
 * @param {string} userData.planType - User's subscription plan (starter, pro, etc.)
 * @param {string} userData.subscriptionStatus - Subscription status (active, canceled, etc.)
 * @param {string} userData.signupDate - Date user signed up
 * @param {string} userData.name - User's name
 * @param {string} userData.loginMethod - How user logged in (Google, Email, etc.)
 * @param {boolean} userData.isFirstTimeIdentification - Whether this is first time identifying this user
 */
export const setUserIdentity = (userData = {}) => {
  const { 
    userId, 
    userEmail, 
    isAuthenticated = false, 
    isPaying = false,
    planType,
    subscriptionStatus,
    signupDate,
    name,
    loginMethod,
    isFirstTimeIdentification = false
  } = userData;

  const logDebug = (...args) => {
    if (typeof console?.debug === 'function') {
      console.debug(...args);
      return;
    }
    if (typeof console?.log === 'function') {
      console.log(...args);
    }
  };
  
  // Skip if no user ID or email
  if (!userId && !userEmail) return;
  
  try {
    // 1. 🆕 POSTHOG USER IDENTIFICATION WITH ALIASING - The main feature!
    if (POSTHOG_KEY && (userId || userEmail)) {
      const posthogClient = getPostHogClient({ initialize: true });
      if (!posthogClient) {
        logDebug('🎯 ⚠️ PostHog client unavailable - user identification skipped');
        return;
      }

      // Use email as the distinct ID if userId is not available
      const distinctId = userId || userEmail;
      
      // Build comprehensive user properties for PostHog
      const posthogProperties = {
        // Core identification
        email: userEmail,
        user_id: userId,
        
        // Authentication & Payment Status  
        is_authenticated: isAuthenticated,
        is_paying_customer: isPaying,
        plan_type: planType || (isPaying ? 'paid' : 'free'),
        subscription_status: subscriptionStatus || (isPaying ? 'active' : 'free'),
        
        // User Profile Info
        name: name,
        login_method: loginMethod,
        signup_date: signupDate,
        
        // Session Info
        session_start: new Date().toISOString(),
        page_url: currentPageUrl(),
        page_path: window.location.pathname,
        
        // Derived Attributes for Segmentation
        user_type: isPaying ? 'paying_customer' : 'free_user',
        customer_segment: isPaying 
          ? (planType === 'pro' ? 'pro_customer' : 'starter_customer')
          : 'free_user'
      };
      
      // Remove undefined values to keep PostHog data clean
      Object.keys(posthogProperties).forEach(key => {
        if (posthogProperties[key] === undefined || posthogProperties[key] === null) {
          delete posthogProperties[key];
        }
      });
      
      // Keep one canonical distinct ID for both first-time and repeat identification.
      // Email remains a person property and is only the fallback when no user ID exists.
      if (posthogClient && typeof posthogClient.identify === 'function') {
        posthogClient.identify(distinctId, posthogProperties);
        if (isFirstTimeIdentification) {
          logDebug(`✅ [PostHog] User identified for the first time: ${distinctId}`);
        }
      }
      
      // Also set person properties for feature flag targeting
      if (posthogClient && typeof posthogClient.setPersonProperties === 'function') {
        posthogClient.setPersonProperties(posthogProperties);
      }
    } else if (!posthogInitialized) {
      logDebug('🎯 ⚠️ PostHog not ready - user identification will be retried');
    }
    
    // 2. Set user ID for Google Analytics 4
    if (window.gtag && GA4_ID && userId) {
      window.gtag('config', GA4_ID, {
        'user_id': userId
      });
    }
    
    // 3. Set user email for Facebook Pixel advanced matching
    if (window.fbq && FACEBOOK_PIXEL_ID && userEmail) {
      window.fbq('init', FACEBOOK_PIXEL_ID, {
        em: userEmail
      });
    }
    
    // 4. 🆕 HOTJAR USER IDENTIFICATION - Keep existing Hotjar integration
    // Use the global hj function when Hotjar is initialized.
    if (typeof window !== 'undefined' && window.hj && userId) {
      // Build comprehensive user attributes for Hotjar
      const hotjarAttributes = {
        // Core identification
        email: userEmail,
        user_id: userId,
        
        // Authentication & Payment Status  
        is_authenticated: isAuthenticated,
        is_paying_customer: isPaying,
        plan_type: planType || (isPaying ? 'paid' : 'free'),
        subscription_status: subscriptionStatus || (isPaying ? 'active' : 'free'),
        
        // User Profile Info
        name: name,
        login_method: loginMethod,
        signup_date: signupDate,
        
        // Session Info
        session_start: new Date().toISOString(),
        page_url: currentPageUrl(),
        page_path: window.location.pathname,
        
        // Derived Attributes for Segmentation
        user_type: isPaying ? 'paying_customer' : 'free_user',
        customer_segment: isPaying 
          ? (planType === 'pro' ? 'pro_customer' : 'starter_customer')
          : 'free_user'
      };
      
      // Remove undefined values to keep Hotjar data clean
      Object.keys(hotjarAttributes).forEach(key => {
        if (hotjarAttributes[key] === undefined || hotjarAttributes[key] === null) {
          delete hotjarAttributes[key];
        }
      });
      
      // Call Hotjar identify
      window.hj('identify', userId, hotjarAttributes);
    } else if (typeof window !== 'undefined' && !window.hj) {
      logDebug('🎯 ⚠️ Hotjar not available (window.hj not found) - user identification skipped');
    }
    
    // 5. Push user data to data layer for GTM
    if (userId || userEmail) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        userId: userId,
        userEmail: userEmail,
        isAuthenticated: isAuthenticated,
        isPaying: isPaying,
        planType: planType,
        subscriptionStatus: subscriptionStatus,
        event: 'userIdentified'
      });
    }
  } catch (error) {
    console.error('Error setting user identity:', error);
  }
};

/**
 * updatePostHogProperties:
 * Updates user properties for PostHog targeting and feature flags
 * 
 * @public
 * @param {Object} properties - User properties for targeting
 */
export const updatePostHogProperties = (properties = {}) => {
  const posthogClient = getPostHogClient({ initialize: true });
  if (!posthogClient) return;
  
  try {
    if (typeof posthogClient.setPersonProperties === 'function') {
      posthogClient.setPersonProperties(properties);
    } else {
      console.warn('❌ [PostHog] setPersonProperties not available');
    }
  } catch (error) {
    console.error('Error updating PostHog properties:', error);
  }
};

/* ==================== External Providers Configuration ==================== */

/**
 * GA4_EVENTS_MAP:
 * 🚨 CRITICAL: Mapping of canonical event names to GA4 event names.
 * These mappings are used for conversion tracking - do not change without updating all systems.
 */
const GA4_EVENTS_MAP = {
  page_view: 'page_view',
  payment_success: 'purchase',       // 🚨 CRITICAL: Used for conversion tracking
  upgrade_to_pro_success: 'purchase', // 🚨 CRITICAL: Generic pro upgrade event -> purchase
  user_authenticated: 'login',
  begin_checkout: 'begin_checkout',
  lead_captured: 'generate_lead',
  lead_capture_submitted: 'generate_lead'
};

/**
 * FACEBOOK_PIXEL_EVENTS_MAP:
 * 🚨 CRITICAL: Mapping of canonical event names to Facebook Pixel events.
 * These mappings are used for conversion tracking - do not change without updating all systems.
 */
const FACEBOOK_PIXEL_EVENTS_MAP = {
  page_view: { event: 'PageView' },
  payment_success: { event: 'Purchase' }, // 🚨 CRITICAL: Used for conversion tracking
  upgrade_to_pro_success: { event: 'Purchase' }, // 🚨 CRITICAL: Generic pro upgrade event -> purchase
  user_authenticated: { event: 'CompleteRegistration' },
  begin_checkout: { event: 'InitiateCheckout' },
  lead_captured: { event: 'Lead' },
  lead_capture_submitted: { event: 'Lead' }
};

/**
 * POSTHOG_EVENTS_MAP:
 * Mapping of canonical event names to PostHog event names.
 * NOTE: upgrade_to_pro_success is NOT mapped so it shows as the original event name in PostHog.
 */
const POSTHOG_EVENTS_MAP = {
  page_view: 'PageView',
  payment_success: 'Purchase',
  user_authenticated: 'CompleteRegistration', 
  begin_checkout: 'InitiateCheckout',
  lead_captured: 'Lead',
  lead_capture_submitted: 'Lead',
  // 🚨 CRITICAL A/B TEST MAPPING - DO NOT CHANGE WITHOUT CRO APPROVAL 🚨
  get_started_click: 'GetStartedClick', // 🚨 CRITICAL: Homepage A/B test primary metric
  pricing_cta_click: 'PricingCTAClick', // 🚨 CRITICAL: Pricing page A/B test metric (STARTER_PRICING_ABC)
  bootcamp_path_selected: 'BootcampPathSelected',
  bootcamp_plan_selected: 'BootcampPlanSelected',
  bootcamp_checkout_started: 'BootcampCheckoutStarted',
  sales_page_view: 'PageView',
  skip_to_starter_plan: 'SkipUpgrade',
  starter_plan_selected: 'SelectPlan',
  signup_survey_completed: 'CompleteOnboarding',
  signup_survey_started: 'StartOnboarding',
  signup_survey_abandoned: 'AbandonOnboarding',
  // 🚨 CRITICAL A/B TEST MAPPING - UPGRADE PAGE BOOK CALL TEST 🚨
  upgrade_page_primary_cta_clicked: 'UpgradePagePrimaryCTA', // 🚨 CRITICAL: Unified A/B test primary metric
  book_expert_call_clicked: 'UpgradePageBookCallClicked', // 🚨 CRITICAL: Primary metric for upgrade page A/B test
  expert_call_booked: 'UpgradePageCallBooked', // 🚨 CRITICAL: Success metric for call booking funnel
  calendly_widget_loaded: 'CalendlyLoaded',
  calendly_widget_error: 'CalendlyError',
  expert_call_booking_success_page_viewed: 'CallBookingSuccess',
  
  // 🚨 CRITICAL A/B TEST MAPPING - UPGRADE PAGE CONTROL EVENTS 🚨
  upgrade_to_pro_cta_clicked: 'UpgradeToProCTAClicked', // 🚨 CRITICAL: Control page CTA clicks
  upgrade_to_pro_success: 'UpgradeToProSuccess' // 🚨 CRITICAL: Actual purchase success events
};

/**
 * 🚨 CRITICAL: Export POSTHOG_EVENTS_MAP for A/B testing validation 
 * This export is required for critical A/B test analytics tests
 */
export { POSTHOG_EVENTS_MAP };

/**
 * GOOGLE_ADS_CONVERSION_LABELS:
 * 🚨 CRITICAL: Mapping of event names to their specific Google Ads conversion labels.
 * Only includes purchase conversions as the signup/begin_checkout labels are impossible to set up in Google Ads.
 */
const GOOGLE_ADS_CONVERSION_LABELS = {
  // Lead conversion - tracks when users submit email/phone
  lead_captured: GOOGLE_ADS_LEAD_LABEL,

  // Purchase conversions - trial starts ($0)
  payment_success: GOOGLE_ADS_PURCHASE_LABEL,
  upgrade_to_pro_success: GOOGLE_ADS_PURCHASE_LABEL, // 🚨 CRITICAL: Generic pro upgrade event -> purchase conversion

  // Note: Payment Received is tracked server-side only (actual money received, not trial starts)
};

// Google Ads conversion string is built dynamically: `${GOOGLE_ADS_CONVERSION_ID}/${LABEL}`

/* ==================== Main Event Tracking Function ==================== */

/**
 * 🚨 CRITICAL: Track conversions across all analytics providers with bulletproof delivery.
 * This function ensures conversions are sent to GA4, Google Ads, and Facebook Pixel.
 * Changes to this function could break conversion tracking - test thoroughly.
 * 
 * @private
 * @param {string} eventName - The canonical event name
 * @param {Object} properties - Event properties including transaction_id, value, currency
 */
const trackConversion = (eventName, properties = {}) => {
  // 1. Log for debugging and monitoring
  // Removed console.log for cleaner logs

  // 2. Send to Google Analytics 4 with enhanced ecommerce data (ALL events)
  if (window.gtag && GA4_ID) {
    const gaEventName = GA4_EVENTS_MAP[eventName] || eventName;
    const gaProperties = {
      ...properties,
      // Ensure required GA4 ecommerce parameters are present
      transaction_id: properties.transaction_id || properties.sessionId,
      value: parseFloat(properties.value) || 0,
      currency: (properties.currency || 'USD').toUpperCase()
    };
    
    window.gtag('event', gaEventName, gaProperties);
  }

  // 3. Send to Google Ads for conversion events (if label is configured)
  if (window.gtag && GOOGLE_ADS_CONVERSION_ID && GOOGLE_ADS_CONVERSION_LABELS[eventName]) {
    const conversionLabel = GOOGLE_ADS_CONVERSION_LABELS[eventName];
    if (conversionLabel) {
      const payload = {
        value: parseFloat(properties.value) || 0,
        currency: (properties.currency || 'USD').toUpperCase(),
        transaction_id: properties.transaction_id || properties.sessionId
      };

      const primarySendTo = `${GOOGLE_ADS_CONVERSION_ID}/${conversionLabel}`;
      window.gtag('event', 'conversion', { send_to: primarySendTo, ...payload });
      console.debug(`🎯 Google Ads conversion sent: ${primarySendTo}`, payload);

      if (
        GOOGLE_ADS_QA_PURCHASE_LABEL &&
        conversionLabel === GOOGLE_ADS_PURCHASE_LABEL
      ) {
        const qaSendTo = `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_QA_PURCHASE_LABEL}`;
        window.gtag('event', 'conversion', { send_to: qaSendTo, ...payload });
        console.debug(`🧪 Google Ads QA conversion sent: ${qaSendTo}`, payload);
      }
    }
  }

  // 4. Send to Facebook Pixel for mapped events  
  if (window.fbq && FACEBOOK_PIXEL_ID && FACEBOOK_PIXEL_EVENTS_MAP[eventName]) {
    const fbConfig = FACEBOOK_PIXEL_EVENTS_MAP[eventName];
    const fbProperties = {
      value: parseFloat(properties.value) || 0,
      currency: (properties.currency || 'USD').toUpperCase(),
      ...properties
    };
    
    window.fbq('track', fbConfig.event, fbProperties);
  }

  // 5. Additional providers can be added here
  // Note: PostHog conversion tracking is handled via event capture
}

/**
 * pushEvent:
 *  - Normalizes event data.
 *  - Pushes the event into a universal data layer.
 *  - Sends every event via trackConversion.
 * 
 * @public
 * @param {Object} config - Event configuration object.
 * Example:
 *   {
 *     event: ANALYTICS_EVENTS.PAYMENT_SUCCESS,
 *     sessionId: 'cs_test_...',
 *     value: 49.99,
 *     currency: 'USD',
 *     userEmail: 'user@example.com'
 *   }
 */
export const pushEvent = (config) => {
  try {
    const safeEvent = sanitizeAnalyticsProperties({
      timestamp: new Date().toISOString(),
      page_location: currentPageUrl(),
      page_referrer: currentReferrer(),
      page_title: document.title,
      ...getAnalyticsContextProperties(),
      ...config,
      event: getEventName(config?.event)
    });

    if (safeEvent.event === 'payment_success' && !safeEvent.event_origin) {
      safeEvent.event_origin = 'frontend';
    }
    const isCriticalPostHogEvent = isPostHogCriticalEvent(safeEvent.event);

    // Enhanced logging for critical events
    if (isCriticalPostHogEvent) {
      console.debug(`🎯 [Analytics] CRITICAL EVENT: ${safeEvent.event}`, safeEvent);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(safeEvent);

    // Send every event via trackConversion
    trackConversion(safeEvent.event, safeEvent);

    /*  👉  ENHANCED PostHog Integration with better error handling and debugging */
    try {
      const posthogClient = getPostHogClient({ initialize: true });

      if (posthogClient && typeof posthogClient.capture === "function") {
        const rawPurchaseValue = safeEvent.value ?? safeEvent.amount;
        const numericPurchaseValue = Number(rawPurchaseValue);
        const hasPurchaseValue = rawPurchaseValue !== undefined && rawPurchaseValue !== null && Number.isFinite(numericPurchaseValue);
        const hasPositivePurchaseValue = hasPurchaseValue && numericPurchaseValue > 0;

        // Use Facebook-style event names for PostHog consistency
        const posthogEventName = safeEvent.event === 'payment_success' && !hasPositivePurchaseValue
          ? 'PaymentSetupCompleted'
          : POSTHOG_EVENTS_MAP[safeEvent.event] || safeEvent.event;
        
        // Enhanced logging for critical events
        if (isCriticalPostHogEvent) {
          console.debug(`🎯 [PostHog] Sending critical event: ${posthogEventName}`, {
            originalEvent: safeEvent.event,
            mappedEvent: posthogEventName,
            properties: safeEvent
          });
      }
        
        // 🚨 CRITICAL FIX: Use email as distinct ID for events with email to ensure funnel continuity
        const eventProperties = {
          ...safeEvent,
          // Add PostHog-specific properties for better tracking
          $source: 'analytics_module',
          $timestamp: safeEvent.timestamp,
          // Ensure critical fields are present
          ...(safeEvent.transaction_id && { transaction_id: safeEvent.transaction_id }),
          ...(hasPurchaseValue && { value: numericPurchaseValue }),
          ...(hasPositivePurchaseValue && { amount_usd: numericPurchaseValue }),
          ...(safeEvent.currency && { currency: safeEvent.currency }),
          ...(safeEvent.email && { email: safeEvent.email })
        };
        
        // 🔍 SIMPLIFIED: Let PostHog handle distinct ID management normally
        // This ensures all events use the same anonymous ID for funnel tracking
        posthogClient.capture(posthogEventName, sanitizeAnalyticsProperties(eventProperties));
        
        // Enhanced logging for critical events
        if (isCriticalPostHogEvent) {
          console.debug(`🎯 [PostHog] Event sent with default distinct ID management`);
        }
        
        // Verify PostHog delivery for critical events
        if (isCriticalPostHogEvent) {
          console.debug(`✅ [PostHog] Event sent successfully: ${posthogEventName}`);

          // Show the current distinct ID for debugging
          const currentDistinctId = posthogClient.get_distinct_id ? posthogClient.get_distinct_id() : 'unknown';
          console.debug(`🔍 [PostHog] Using distinct ID: ${currentDistinctId}`);
        }
        
      } else {
        // 🚨 EMERGENCY: Try to recover PostHog if it's missing but environment variables are set
        if (POSTHOG_KEY && !window.posthog && isCriticalPostHogEvent) {
          console.warn(`🚨 [PostHog] EMERGENCY RECOVERY: PostHog missing for critical event ${safeEvent.event}, attempting to reinitialize...`);
          
          // Try to reinitialize PostHog
          try {
            initPostHog();
            
            // Wait a bit and try again
            setTimeout(() => {
              const recoveredPostHogClient = getPostHogClient({ attachToWindow: true });
              if (recoveredPostHogClient && typeof recoveredPostHogClient.capture === "function") {
                const posthogEventName = POSTHOG_EVENTS_MAP[safeEvent.event] || safeEvent.event;
                console.debug(`🔄 [PostHog] RECOVERY SUCCESS: Sending event after reinit: ${posthogEventName}`);
                
                const recoveryEventProperties = {
                  ...safeEvent,
                  $source: 'analytics_module_recovery',
                  $timestamp: safeEvent.timestamp,
                  recovery_attempt: true
                };
                
                // Use standard recovery approach
                recoveredPostHogClient.capture(posthogEventName, recoveryEventProperties);
                console.debug(`🔄 [PostHog] Recovery event sent with default distinct ID`);
              } else {
                console.error(`❌ [PostHog] RECOVERY FAILED: Still no PostHog after reinit attempt`);
              }
            }, 100);
          } catch (recoveryError) {
            console.error(`❌ [PostHog] RECOVERY ERROR:`, recoveryError);
          }
        }
        
        // Enhanced debugging for PostHog issues
        if (isCriticalPostHogEvent) {
          console.warn(`⚠️ [PostHog] PostHog not available for critical event: ${safeEvent.event}`, {
            hasWindow: typeof window !== 'undefined',
            hasPosthog: !!(typeof window !== 'undefined' && window.posthog),
            hasCapture: !!(typeof window !== 'undefined' && window.posthog && typeof window.posthog.capture === "function"),
            posthogType: typeof window !== 'undefined' && window.posthog ? typeof window.posthog : 'undefined',
            posthogKey: POSTHOG_KEY ? 'SET' : 'MISSING',
            windowKeys: typeof window !== 'undefined' ? Object.keys(window).filter(k => k.includes('posthog') || k.includes('ph')).slice(0, 5) : 'no window'
          });
        }
      }
    } catch (posthogError) {
      console.error(`❌ [PostHog] Error sending event ${safeEvent.event}:`, posthogError);
      
      // For critical events, also log to console for debugging
      if (isCriticalPostHogEvent) {
        console.error(`🚨 [PostHog] CRITICAL EVENT FAILED: ${safeEvent.event}`, {
          error: posthogError,
          event: safeEvent,
          posthogState: typeof window !== 'undefined' && window.posthog ? 'available' : 'not_available'
        });
      }
    }

    if (process.env.NODE_ENV === 'development' && safeEvent.event === 'unknown_event') {
      console.warn(`Unknown analytics event: ${JSON.stringify(config?.event)}`);
    }
  } catch (err) {
    console.error('❌ [Analytics] Critical error in pushEvent:', err);
    
    // For critical events, ensure we log the failure
    if (isPostHogCriticalEvent(getEventName(config?.event))) {
      console.error(`🚨 [Analytics] CRITICAL EVENT PROCESSING FAILED:`, {
        error: err,
        originalConfig: config
      });
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.error('Analytics event error:', err);
    }
  }
};

/* ==================== Provider Initialization Functions ==================== */

/**
 * initGTM:
 * Initializes the GTM provider by creating the data layer and injecting
 * the GTM script asynchronously. GTM is only one output source.
 * 
 * @public
 */
export const initGTM = () => {
  if (!GTM_ID) {
    console.warn('GTM_ID not set – GTM provider disabled');
    return;
  }
  window.dataLayer = window.dataLayer || [];
  try {
    (function(w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l !== 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', GTM_ID);
    console.debug('GTM provider initialized');
  } catch (initErr) {
    console.error('GTM provider initialization failed:', initErr);
  }
};

/**
 * initAnalytics:
 * Initializes all configured analytics providers.
 * 
 * Call this function once during application startup.
 * 
 * @public
 * @param {Object} userAttributes - Optional user attributes for PostHog targeting
 */
export const initAnalytics = (userAttributes = {}) => {
  if (typeof window !== 'undefined' && window.__BOTSPOT_PRERENDER__) {
    return;
  }
  // Initialize PostHog for analytics and feature flags
  initPostHog(userAttributes);
  
  // Initialize Facebook Pixel if not already done
  if (typeof window !== 'undefined' && window.fbq && !window.fbq.loaded) {
    window.fbq('init', FACEBOOK_PIXEL_ID);
  }
};

/* ==================== Development Helpers & Best Practices ==================== */

/**
 * Development Notes:
 * 1. All events are logged to the console in development mode.
 * 2. Warnings are issued for missing or unknown events.
 * 3. Provider initialization and event tracking errors are caught gracefully.
 * 
 * Best Practices:
 * - Always use ANALYTICS_EVENTS constants when calling pushEvent.
 *   Example: pushEvent({ event: ANALYTICS_EVENTS.PAGE_VIEW, page_title: 'Home' });
 * - Include contextual parameters (e.g., 'source') with every event.
 * - For high-value conversion events (e.g., PAYMENT_SUCCESS), ensure that a mapping
 *   exists in AD_PROVIDERS_MAP so that events are forwarded to external providers.
 * - Extend AD_PROVIDERS_MAP to add new output sources as needed.
 * 
 * Common Patterns:
 * - Use helper functions to normalize and resolve event data.
 * - Monitor console logs during development to diagnose issues.
 */

/**
 * End of General Analytics Module.
 * 
 * Integration Instructions:
 * 1. Import and call initAnalytics() once at application startup.
 * 2. Use pushEvent({...}) to record events throughout the application.
 * 3. Ensure that necessary provider snippets (gtag.js, fbq, etc.) are included in your index.html.
 * 4. Configure your environment variables for REACT_APP_GTM_ID, and REACT_APP_FACEBOOK_PIXEL_ID.
 */

/**
 * Generate consistent transaction ID for lead capture events
 * This ensures frontend and backend use the same transaction ID for deduplication
 * 
 * @param {string} email - User's email address
 * @param {string} eventType - Type of event (lead_capture, signup, etc.)
 * @returns {string} Consistent transaction ID
 */
export const generateLeadTransactionId = (email, eventType = 'lead_capture') => {
  if (!email) {
    return `${eventType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  // Create consistent hash based on email and current date (not time)
  // This allows some deduplication within the same day while allowing tracking across days
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const baseString = `${email.toLowerCase().trim()}_${eventType}_${today}`;
  
  // Simple hash function for consistency (not cryptographic)
  let hash = 0;
  for (let i = 0; i < baseString.length; i++) {
    const char = baseString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return `${eventType}_${Math.abs(hash)}_${today.replace(/-/g, '')}`;
};

/**
 * Generate consistent transaction ID for checkout events
 * 
 * @param {string} paymentIntentId - Stripe payment intent ID
 * @param {string} subscriptionId - Stripe subscription ID  
 * @param {string} fallback - Fallback ID if no Stripe IDs available
 * @returns {string} Transaction ID
 */
export const generateCheckoutTransactionId = (paymentIntentId, subscriptionId, fallback) => {
  return paymentIntentId || subscriptionId || fallback || `checkout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * identifyUserFromLeadCapture:
 * 🎯 CONVENIENCE HELPER: Call this when a user provides their email for the first time
 * (lead forms, signup forms, checkout forms, etc.)
 * 
 * This automatically handles the identify pattern with a direct identify call for proper funnel tracking.
 * 
 * @public
 * @param {string} userEmail - User's email address
 * @param {Object} additionalData - Additional user data (name, phone, source, etc.)
 * @param {string} source - Where the lead capture happened (e.g., 'checkout_form', 'signup_form')
 */
export const identifyUserFromLeadCapture = (userEmail, additionalData = {}, source = 'unknown') => {
  if (!userEmail) {
    console.warn('🎯 Cannot identify user without email');
    return;
  }

  // Build comprehensive user properties
  const properties = {
    email: userEmail,
    name: additionalData.name,
    phone: additionalData.phone,
    lead_type: additionalData.lead_type || 'unknown',
    source: source,
    form_step: additionalData.form_step || 'lead_capture',
    first_identified_at: new Date().toISOString(),
    identified_from: source,
    ...additionalData
  };

  console.debug('🎯 [PostHog] Starting user identification for lead capture:', {
    email: userEmail,
    source: source,
    hasPosthog: !!(typeof window !== 'undefined' && window.posthog)
  });

  if (typeof window !== 'undefined' && window.posthog) {
    try {
      console.debug('🎯 [PostHog] Direct identification (alias removed):', userEmail);
      if (typeof window.posthog.identify === 'function') {
        window.posthog.identify(userEmail, properties);
        console.debug('✅ [PostHog] User identification complete');
      }
    } catch (error) {
      console.error('❌ [PostHog] Error identifying user:', error);

      // Fallback: try simple identify
      try {
        if (typeof window.posthog.identify === 'function') {
          console.debug('🔄 [PostHog] Fallback identification:', userEmail);
          window.posthog.identify(userEmail, properties);
        }
      } catch (fallbackError) {
        console.error('❌ [PostHog] Fallback identification also failed:', fallbackError);
      }
    }
  } else if (posthog && typeof posthog.identify === 'function') {
    // Fallback to imported posthog
    try {
      console.debug('🎯 [PostHog] Using imported posthog for identification:', userEmail);
      posthog.identify(userEmail, properties);
    } catch (error) {
      console.error('❌ [PostHog] Error with imported posthog.identify():', error);
    }
  } else {
    console.warn('⚠️ [PostHog] PostHog identify not available', {
      hasWindow: typeof window !== 'undefined',
      hasPosthog: !!(typeof window !== 'undefined' && window.posthog),
      hasImportedPosthog: !!posthog
    });
  }
};
